import { supabase } from '../lib/supabase';
import { Formatters } from '../shared/lib/formatters';
import { PageFooter } from '../shared/components';
import { slackConnectionRow, slackNotificationsCard, mountSlackSettings } from '../shared/components/SlackSettings';   // R109 (l)
import { StatementBuilder } from './statementBuilder/StatementBuilder';   // R119
import { FORMULA_BOUNDS, PRESETS_FALLBACK, PRESET_IDS, formulaColumns, isLegacy, presetOf, presetName, shapeChips, fmtMoney, EXPLAIN, scheduleText } from '../shared/lib/statementLadder';
import type { Preset } from '../shared/lib/statementLadder';

// ── Constants ────────────────────────────────────────────────────────────────

const LINE_ITEM_TYPES = [
  { code: 'base_rate',             label: 'Base Rate',              category: 'Accommodation' },
  { code: 'cleaning_fee',          label: 'Cleaning Fee',           category: 'Fees' },
  { code: 'pet_fee',               label: 'Pet Fee',                category: 'Fees' },
  { code: 'eci',                   label: 'Early Check-in',         category: 'Fees' },
  { code: 'lco',                   label: 'Late Check-out',         category: 'Fees' },
  { code: 'resort_fee',            label: 'Resort Fee',             category: 'Fees' },
  { code: 'extra_guest_fee',       label: 'Extra Guest Fee',        category: 'Fees' },
  { code: 'damage_waiver',         label: 'Damage Waiver',          category: 'Fees' },
  { code: 'damage_charge',         label: 'Damage Charge',          category: 'Fees' },
  { code: 'cancellation_fee',      label: 'Cancellation Fee',       category: 'Fees' },
  { code: 'insurance_fee',         label: 'Insurance Fee',          category: 'Fees' },
  { code: 'service_fee',           label: 'Service Fee',            category: 'Fees' },
  { code: 'other_fees',            label: 'Other Fees',             category: 'Fees' },
  { code: 'management_fee_airbnb', label: 'Mgmt Fee (Airbnb)',      category: 'Fees' },
  { code: 'linen_fee_airbnb',      label: 'Linen Fee (Airbnb)',     category: 'Fees' },
  { code: 'community_fee_airbnb',  label: 'Community Fee (Airbnb)', category: 'Fees' },
  { code: 'parking_fee',           label: 'Parking Fee',            category: 'Fees' },
  { code: 'discount',              label: 'Discount',               category: 'Discounts' },
  { code: 'tax_lodging',           label: 'Lodging Tax',            category: 'Taxes' },
  { code: 'tax_local',             label: 'Local Tax',              category: 'Taxes' },
  { code: 'tax_other',             label: 'Other Tax',              category: 'Taxes' },
  { code: 'tax_transient_occupancy', label: 'TOT',                  category: 'Taxes' },
  { code: 'tax_city_tourism',      label: 'City Tourism Tax',       category: 'Taxes' },
  { code: 'tax_sales',             label: 'Sales Tax',              category: 'Taxes' },
  { code: 'tax_hotel',             label: 'Hotel Tax',              category: 'Taxes' },
  { code: 'tax_vat_gst',           label: 'VAT / GST',              category: 'Taxes' },
  { code: 'channel_host_fee',      label: 'Channel Host Fee',       category: 'Channel' },
  { code: 'channel_guest_fee',     label: 'Channel Guest Fee',      category: 'Channel' },
  { code: 'damage_deposit',        label: 'Damage Deposit',         category: 'Deposits' },
] as const;

// R119: the treatments in words (the stored codes are unchanged). Today's
// WCH_INCLUDE_CLEAN_20 edit picked "Bypass to Owner" for cleaning meaning "the
// owner's" — the code means owner's money with NO commission, and it moved 20
// properties at once. Each label now says what it does to the owner's money.
const TREATMENT_OPTIONS = [
  { value: 'commissionable',         label: 'Owner\'s — commission charged',                 cls: 'trt-comm'  },
  { value: 'passthrough_to_company', label: 'Company keeps',                                  cls: 'trt-pass'  },
  { value: 'bypass_to_owner',        label: 'Owner\'s — NO commission (Non-Commissionable)', cls: 'trt-owner' },
  { value: 'bypass_to_jurisdiction', label: 'Tax — paid to the jurisdiction',                 cls: 'trt-juri'  },
  { value: 'excluded',               label: 'Not counted',                                    cls: 'trt-excl'  },
] as const;

type Treatment = typeof TREATMENT_OPTIONS[number]['value'];

function trtChip(t: string): string {
  const opt = TREATMENT_OPTIONS.find(o => o.value === t);
  if (!opt) return `<span class="trt trt-excl">${t}</span>`;
  return `<span class="trt ${opt.cls}">${opt.label}</span>`;
}

function trtSelect(name: string, value: string): string {
  return `<select name="${name}" class="cfg-trt-sel">
    <option value="">—</option>
    ${TREATMENT_OPTIONS.map(o => `<option value="${o.value}" ${value === o.value ? 'selected' : ''}>${o.label}</option>`).join('')}
  </select>`;
}

// R118: the rules POST/PUT /api/formulas enforce, checked in the modal first
// so a refused save is said next to the button (not a silent nothing).
const FORMULA_CODE_RX = /^[A-Z0-9_]{3,40}$/;
function formulaInputError(p: Record<string, any>, ratePct: number, isEdit: boolean): { field: string; msg: string } | null {
  if (!isEdit && !FORMULA_CODE_RX.test(p.formula_code || '')) return { field: 'formula_code', msg: 'Formula code: 3 to 40 letters, numbers or _ (e.g. CUSTOM_20_BASE).' };
  if (!p.formula_name) return { field: 'formula_name', msg: 'Give the formula a name.' };
  if (!(Number.isFinite(ratePct) && ratePct > 0 && ratePct <= 100)) return { field: 'commission_rate', msg: 'Commission rate must be more than 0% and at most 100%.' };
  if (p.minimum_fee_per_booking != null && !(Number.isFinite(p.minimum_fee_per_booking) && p.minimum_fee_per_booking >= 0)) return { field: 'minimum_fee_per_booking', msg: 'Minimum fee per booking must be a dollar amount, or blank for none.' };
  return null;
}

// ── statement formula helpers (R118 called them "gross revenue formulas"; R119
// made each one the whole statement ladder — Gross Revenue is one of its columns) ──
// Channel-fee rate keys: the store's channel_rates (+ 'other'); a property's
// own profile carries the four named channels only (the shared contract).
const GRF_CH: Array<[string, string]> = [['airbnb', 'Airbnb'], ['vrbo', 'Vrbo'], ['bookingcom', 'Booking.com'], ['direct', 'Direct'], ['other', 'Other channels']];
const GRF_PCH = GRF_CH.filter(([k]) => k !== 'other');
const GRF_BUILTIN = 'statement_default';   // lib/ownerFinance DEFAULT_RULE — always present, never deleted
// R118 review #17: a property profile's limits — EXACTLY lib/ownerFinance
// normProfile's numIn bounds (scripts/test_gross_ui_dom.mjs pins them to it).
const GRF_PROFILE_MAX = { company_fee: 5000, tot_pct: 30, commission_rate: 60, channel: 50 };
// R118 review #2: the direct / Booking.com tax % (tot_pct_direct / tot_pct_bookingcom)
// share tot_pct's bound — normProfile reads all three with numIn(…, 0, 30).
const GRF_TAX_KEYS = ['tot_pct', 'tot_pct_direct', 'tot_pct_bookingcom'] as const;
type GrfNumKey = 'company_fee' | 'commission_rate' | typeof GRF_TAX_KEYS[number];
const grfMax = (k: GrfNumKey): number => (k === 'company_fee' ? GRF_PROFILE_MAX.company_fee : k === 'commission_rate' ? GRF_PROFILE_MAX.commission_rate : GRF_PROFILE_MAX.tot_pct);
const grfEsc = (v: unknown): string => Formatters.escapeHtml(String(v == null ? '' : v));
const grfNum = (v: unknown): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const grfPct = (v: unknown): string => { const n = grfNum(v); return n == null ? '—' : `${Math.round(n * 10) / 10}%`; };
// R119 review #16: what a commission (revenue) formula reaches on the OWNER PORTAL (lib/ownerFinance
// resolveColumns) — used: its properties; auto: those on an Automatic statement formula (they take
// cleaning placement, channel-fee base, commission base and rate from it); part: those on another
// statement formula with an Automatic cleaning or commission-base column (that column follows it);
// rate: the rest that take its rate (no fixed rate in the statement formula, none in the facts)
interface RevReach { used: number; auto: number; part: number; rate: number }
const REV_AUTO_WHAT = 'cleaning placement, channel-fee base, commission base and rate';
const grfProps = (n: number): string => `${n} propert${n === 1 ? 'y' : 'ies'}`;
const grfIsAre = (n: number): string => (n === 1 ? 'is' : 'are');
// the legend's count line (the modal's "Line Item Treatments")
function revReachLine(n: RevReach | null): string {
  if (!n) return '';
  if (!n.used) return ' No property uses it yet.';
  // R119 fix-verify (#16, round 4): grammar — "Its 1 property is not…", "None of its 3 is…", and a
  // count after "None" isn't "more"
  if (n.used === 1) return n.auto ? ' Its 1 property is on an Automatic statement formula.'
    : n.part ? ' Its 1 property is on a statement formula with an Automatic cleaning or commission-base column.'
    : n.rate ? ' Its 1 property takes its rate.' : ' Its 1 property is not on an Automatic statement formula.';
  const head = ` ${n.auto ? n.auto : 'None'} of its ${grfProps(n.used)} ${n.auto ? grfIsAre(n.auto) : 'is'} on an Automatic statement formula`;
  const more = [n.part ? `${n.part}${n.auto ? ' more' : ''} ${grfIsAre(n.part)} on one with an Automatic cleaning or commission-base column` : '', n.rate ? `${n.rate}${n.auto || n.part ? ' more' : ''} take${n.rate === 1 ? 's' : ''} its rate` : ''].filter(Boolean);
  return `${head}${more.length ? `; ${more.join('; ')}` : ''}.`;
}
// R119 fix-verify (#16, third pass): plain words that hold on every path — exactly where a
// treatment lands depends on the statement formula's columns and the property's facts
// (lib/ownerFinance resolveColumns / lineSums / reservationMoney: e.g. a statement formula
// that keeps owner fees for the company settles the pet fee; "Cleaning counts in gross: no"
// alone leaves whose the cleaning is to the treatment; the resort fee is the owner's by
// code). So the copy says what CAN move and points at the Preview — never "always"
const REV_FACTS = ' A rate the statement formula fixes, or the property\'s own facts, can override what it sets.';
const REV_TRT_TXT = 'its other line-item treatments can move the owner portal too (a pet fee, rent and discounts, sometimes cleaning) wherever the statement formula and the property\'s facts don\'t settle that item themselves — once saved, a statement formula\'s Preview shows the exact effect. (A month with a published statement shows the statement\'s totals; a stay the statement doesn\'t carry still shows this formula\'s estimate.)';
// R119 fix-verify (#16): its minimum reaches every property whose statement formula sets none
// (resolveColumns min_fee: the formula's, else the revenue formula's — no fact overrides it)
const REV_MIN = ' Its minimum fee per booking applies on the owner portal of every property on it whose statement formula sets none.';
// the "used by N properties" confirm's owner-portal sentence (minFee: this formula has a minimum;
// used: the count the confirm names — the reach count only words "all N" when it agrees)
const REV_TRT_ALL = (n: number | null): string => ` On ${n == null ? 'every property on it' : n === 2 ? 'both of them' : `all ${n} of them`}, ${REV_TRT_TXT}`;
// R119 fix-verify (#16, round 4): clearing a minimum moves those portals too
const REV_MIN_CLEARED = ' Clearing its minimum fee per booking can change the owner portal of every property on it whose statement formula sets none (wherever the minimum was lifting the fee).';
function revReachConfirm(n: RevReach | null, minFee: boolean | 'cleared' = false, used: number | null = null): string {
  const min = minFee === 'cleared' ? REV_MIN_CLEARED : minFee ? REV_MIN : '';
  if (!n || !n.used) return ` Properties on an Automatic statement formula follow it on the owner portal too (${REV_AUTO_WHAT}); on any other statement formula a column set to Automatic (cleaning or commission base) follows it too, and so does the rate unless that formula fixes one.${REV_FACTS}${REV_TRT_ALL(null)}${min}`;
  // R119 fix-verify (#16): "all N" only when the reach count is the confirm's own (≥ 2) — else
  // (no open assignment counted, or a different count) "every property on it"
  const all = n.used >= 2 && (used == null || used === n.used) ? n.used : null;
  // round 4: when the counts drift, speak only for the properties the reach classified
  const of = used != null && used !== n.used ? `of the ${grfProps(n.used)} counted` : 'of them';
  const auto = n.auto
    ? ` ${n.auto} ${of} ${grfIsAre(n.auto)} on an Automatic statement formula, so ${n.auto === 1 ? 'its' : 'their'} owner portal follows it too (${REV_AUTO_WHAT}).`
    : ` None ${of} is on an Automatic statement formula.`;
  // round 4: a statement formula with an Automatic column also takes this rate unless it or the facts fix one
  const part = n.part ? ` ${n.part}${n.auto ? ' more' : ''} ${grfIsAre(n.part)} on a statement formula with an Automatic cleaning or commission-base column, which follows it on the owner portal — and so does its rate, unless that statement formula or the property's facts fix one.` : '';
  const rate = n.rate ? ` ${n.rate}${n.auto || n.part ? ' more' : ''} take${n.rate === 1 ? 's' : ''} its commission rate on the owner portal (no fixed rate in the statement formula, none in the property's facts).` : '';
  if (n.auto || n.part || n.rate) return `${auto}${part}${rate}${n.auto || n.part ? REV_FACTS : ''}${REV_TRT_ALL(all)}${min}`;
  return `${auto}${REV_TRT_ALL(all)}${min}`;
}
const grfMoney = (v: unknown): string => { const n = grfNum(v); return n == null ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`; };
const grfGap = (v: unknown): string => { const n = Number(v) || 0; return Math.abs(n) < 0.005 ? 'matches' : `${n >= 0 ? '+' : '−'}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
// R119 fix-verify: the "Also assign the learned statement formula" tooltip — lib/ownerCalibrate
// target() never replaces a statement formula chosen by hand, "Also replace" or not (review #5);
// overwriteManual replaces hand-set FACTS, whose guard can hold the assign back (review #6)
const GRF_ASSIGN_TIP = 'Points each ticked property at its suggested statement formula (creating the preset formulas it needs) where the learned formula matches at least as many statements. A statement formula someone assigned by hand is always kept — “Also replace the ones set by hand” doesn\'t change that (pick another in Property Settings, or use the builder\'s Learn → Assign). “Also replace” replaces facts set by hand, which can let a property\'s suggested formula be assigned too. Each row says what Apply will do.';
// The last n calendar months before this one, oldest first (YYYY-MM).
const grfMonthsBack = (n: number): string[] => { const out: string[] = []; const d = new Date(); d.setDate(1); for (let i = 0; i < n; i++) { d.setMonth(d.getMonth() - 1); out.unshift(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); } return out; };

// ── Page ─────────────────────────────────────────────────────────────────────

export class SettingsPage {
  private container: HTMLElement;
  private settings: any  = {};
  private feeTypes:    any[] = [];
  private formulas:    any[] = [];
  private expandedFormulas = new Set<string>();
  // R118: the old per-listing assign modal (assignments table, openAssignModal,
  // bindAssignments…) was unreachable since V8 #1 — nothing bound or rendered
  // it — and is gone. Assignment lives in Portfolio → Property Settings.
  private formulaSaving = false;   // R118: one Create / Save in flight at a time
  // R118 review #15: POST / PUT / DELETE /api/formulas are admin + director
  // (formulas are money rules), but Admin → Formulas & system mounts this page
  // for managers / remote ops too — they saw + New Formula / Edit / Del that
  // could only end in "Forbidden". Read at render (the finGate pattern, so a
  // view-as switch follows); anyone else gets the library read-only + why.
  private canWriteFormulas = false;

  // R118 (Jesse: "I do not see any area(s) to setup the gross revenue formulas
  // that I can assign to properties.") — the gross formulas live HERE now, next
  // to the revenue Formula Library (they were a separate P&L Review tab Jesse
  // never opened). Same 'owner_gross_rules' store; per-item saves.
  // R121 (Jesse: "I am also confused on where to build the Gross Revenue formula"):
  // what R118 called the "gross revenue formula" IS the R119 statement formula —
  // the section says so by name, and explains Statement vs Commission (P&L).
  private gross: any = null;            // GET /api/owner-finance/rules (null until loaded)
  private grossHidden = false;          // 401/403 → not admin/director → no gross section at all
  private grossErr = '';
  private grossNote = '';
  private grossNoteBad = false;
  private grossBusy = false;            // one gross write at a time (double-clicks)
  private grossRatesDraft: Record<string, string> | null = null;   // unsaved channel-rate inputs
  private grossCalib: any = null;       // the last /calibrate answer (or { error })
  private grossCalibMonths = '';        // '' = every published month, else '3' | '6' | '12' months back
  private grossOverwriteManual = false;
  private grossRec: any = null;
  private grossRecMonth = (() => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; })();
  private grossModal: null | { kind: 'assign' | 'profile'; id: string; initial?: Set<string> } = null;
  private grossPick = new Set<string>();
  private grossPickSearch = '';
  // R119: "Match the statements" — rows the office unticked (left out of Apply)
  // and "Also assign the learned statement formula" (calibrate assign_formulas)
  private grossCalibUntick = new Set<string>();
  private grossCalibAssign = false;
  // R119: the statement formula builder (New / Edit / Preview / Duplicate, and
  // Property Settings' "Preview · Learn" hand-off); one per render
  private sb: StatementBuilder | null = null;

  // slim = mounted inside Money → Formulas & Fees: definitions only. Data Sync
  // + API Connections render only in Admin (full mode) — UI/UX V8 #1.
  private slim = false;

  constructor(container: HTMLElement, opts?: { slim?: boolean }) {
    this.container = container;
    this.slim = !!(opts && opts.slim);
  }

  async render(): Promise<void> {
    // assignments moved to Property Settings (V8 #1) — no longer fetched here
    // R118: + the gross formulas (403 for anyone but admin/director → hidden)
    await Promise.all([this.loadSettings(), this.loadFeeTypes(), this.loadFormulas(), this.loadGross()]);
    this.canWriteFormulas = (() => { try { const r = sessionStorage.getItem('userRole') || ''; return r === 'admin' || r === 'director'; } catch { return false; } })();
    this.sb = null;   // R119: the old overlay goes with the old markup
    this.container.innerHTML = this.buildPage();
    this.bindAll();
    this.takeStatementFocus();   // R119: Property Settings' "Preview · Learn"
    this.takeCommissionFocus();  // R121: Property Settings' "Edit" beside Commission (P&L)
    if (!this.slim) void mountSlackSettings();   // R109 (l): Slack status row + routing card (admin view only)
    await this.loadStats();
  }

  // ── HTML ──────────────────────────────────────────────────────────────────

  private buildPage(): string {
    return `
    <div class="page-content">

      <div class="dash-head">
        <h1>Configuration</h1>
        <p>Connect data sources, set portfolio-wide defaults, and define the revenue formulas that split each booking between owner and company.</p>
      </div>

      <!-- Row 1: Data Sync + API Connections — Admin only (slim hides them) -->
      ${this.slim ? '' : `
      <div class="cfg-twin">

        <div class="cfg-card">
          <div class="cfg-card-hdr">
            <div class="cfg-icon" style="background:#f0fdf4;">🔄</div>
            <div>
              <div class="cfg-card-title">Data Sync</div>
              <div class="cfg-card-desc">Pull fresh data from connected services</div>
            </div>
          </div>
          <div class="cfg-card-body">
            <div class="cfg-row">
              <div>
                <div class="cfg-row-lbl">Hostaway Listings</div>
                <div class="cfg-row-desc">Properties, URLs, and metadata</div>
              </div>
              <div class="cfg-row-ctrl">
                <button class="btn btn-outline btn-sm cfg-sync-btn" id="sync-hostaway-btn">Sync</button>
              </div>
            </div>
            <div class="cfg-row">
              <div>
                <div class="cfg-row-lbl">Reservations</div>
                <div class="cfg-row-desc">Latest bookings from Hostaway</div>
              </div>
              <div class="cfg-row-ctrl">
                <button class="btn btn-outline btn-sm cfg-sync-btn" id="sync-reservations-btn">Sync</button>
              </div>
            </div>
          </div>
        </div>

        <div class="cfg-card">
          <div class="cfg-card-hdr">
            <div class="cfg-icon" style="background:#eff6ff;">🔌</div>
            <div>
              <div class="cfg-card-title">API Connections</div>
              <div class="cfg-card-desc">External service status</div>
            </div>
          </div>
          <div class="cfg-card-body">
            <div class="cfg-row">
              <div>
                <div class="cfg-row-lbl">Hostaway</div>
                <div class="cfg-row-desc">Property management system</div>
              </div>
              <div class="cfg-row-ctrl">
                <span class="cfg-status-dot on"></span>
                <span class="pill pill-green">Connected</span>
              </div>
            </div>
            <div class="cfg-row">
              <div>
                <div class="cfg-row-lbl">PriceLabs</div>
                <div class="cfg-row-desc">Occupancy &amp; pricing data</div>
              </div>
              <div class="cfg-row-ctrl">
                <span class="cfg-status-dot off"></span>
                <span class="pill pill-gray">Not configured</span>
              </div>
            </div>
            ${slackConnectionRow()}
            <div class="cfg-row">
              <div>
                <div class="cfg-row-lbl">Database</div>
                <div class="cfg-row-desc"><span id="db-listings-count">—</span> listings · <span id="db-reservations-count">—</span> reservations</div>
              </div>
              <div class="cfg-row-ctrl">
                <span class="cfg-status-dot on"></span>
                <span class="pill pill-green">Supabase</span>
              </div>
            </div>
          </div>
        </div>

      </div>
      ${slackNotificationsCard()}`}

      <!-- Row 2: Default Settings -->
      <div class="cfg-card" style="margin-bottom:16px;">
        <div class="cfg-card-hdr">
          <div class="cfg-icon" style="background:#fefce8;">💼</div>
          <div>
            <div class="cfg-card-title">Default Settings</div>
            <div class="cfg-card-desc">Portfolio-wide fallback values for listings without a formula assignment</div>
          </div>
        </div>
        <div class="cfg-card-body">
          <div class="cfg-row">
            <div>
              <div class="cfg-row-lbl">Default Management Fee</div>
              <div class="cfg-row-desc">Applied when a listing has no formula assigned</div>
            </div>
            <div class="cfg-row-ctrl">
              <input type="number" class="cfg-num-input" id="default-mgmt-fee"
                value="${this.settings.default_mgmt_fee_percent || 20}" min="0" max="100">
              <span style="font-size:12px; color:var(--text-muted);">%</span>
              <button class="btn btn-sm btn-secondary" id="save-mgmt-fee">Save</button>
            </div>
          </div>
          <div class="cfg-row">
            <div>
              <div class="cfg-row-lbl">Target Expense Ratio</div>
              <div class="cfg-row-desc">Expected operating costs as % of gross — used in profitability analysis</div>
            </div>
            <div class="cfg-row-ctrl">
              <input type="number" class="cfg-num-input" id="default-expense-ratio"
                value="${this.settings.default_expense_ratio_percent || 72}" min="0" max="100">
              <span style="font-size:12px; color:var(--text-muted);">%</span>
              <button class="btn btn-sm btn-secondary" id="save-expense-ratio">Save</button>
            </div>
          </div>
          <!-- R48 contracts folder link — admin tab only (slim = Money mount, no admin config) -->
          ${this.slim ? '' : `
          <div class="cfg-row">
            <div>
              <div class="cfg-row-lbl">Company contracts folder</div>
              <div class="cfg-row-desc">Drive folder with all signed management agreements${this.settings.admin_contracts_url ? ` · <a href="${Formatters.escapeHtml(this.settings.admin_contracts_url)}" target="_blank" rel="noopener">open ↗</a>` : ''}</div>
            </div>
            <div class="cfg-row-ctrl">
              <input type="text" class="input" id="admin-contracts-url" style="width:260px;"
                value="${Formatters.escapeHtml(this.settings.admin_contracts_url || '')}" placeholder="Google Drive link…">
              <button class="btn btn-sm btn-secondary" id="save-contracts-url">Save</button>
            </div>
          </div>`}
        </div>
      </div>

      <!-- Formula Library -->
      <div class="pf-section" style="margin-bottom:16px;">
        <div class="pf-section-hdr">
          <div>
            <h3>📋 Formula Library — commission formulas (P&amp;L)</h3>
            <p>Commission formulas split each booking between owner and company for the company P&amp;L — how each line item is treated, and the rate. Assign them per property in Portfolio → Property Settings (the “Commission (P&amp;L)” picker).</p>
            <p id="formula-not-gross" style="margin-top:4px;">This is not where Gross Revenue is built: owners' Gross Revenue is a column of the <a href="#grf-root" style="color:var(--info);">statement formulas</a> below.</p>
          </div>
          ${this.canWriteFormulas ? '<button class="btn btn-primary btn-sm" id="new-formula-btn">+ New Formula</button>' : ''}
        </div>
        ${this.canWriteFormulas ? '' : `<div id="formula-readonly-note" role="note" style="padding:10px 20px; font-size:12.5px; color:var(--text-muted); border-bottom:1px solid var(--border-soft);">🔒 Read-only — creating, editing and deleting formulas is limited to admin and director (formulas are money rules). Ask one of them to change a formula; you can still assign formulas to properties in Portfolio → Property Settings.</div>`}
        <div id="formula-library-content">${this.renderFormulaTable()}</div>
      </div>

      <!-- R119/R121: statement formulas, where Gross Revenue is built (R118 named them "gross revenue formulas"; admin/director only — the GET 403s for anyone else) -->
      ${this.grossSectionHtml()}

      <!-- Listing assignments moved (V8 #1): per-property config now has ONE home. -->
      <div class="cfg-card" style="margin-bottom:16px;">
        <div class="cfg-card-body" style="padding:14px 18px; font-size:13px; color:var(--text-muted);">
          🏠 <strong>Assigning formulas</strong> — the commission formula and the statement formula are assigned per property (with bulk assign) in
          <a href="#" id="goto-property-settings" style="color:var(--info);">Portfolio → Property Settings</a>${this.gross ? ', or in bulk from each statement formula above with “Assign…”' : ''}.
          This page keeps the formula and fee <em>definitions</em>.
        </div>
      </div>

      <!-- Metric names & formulas: rename any KPI (GOR -> whatever), describe
           its formula; the glossary and Forecast metric cards update everywhere. -->
      <div class="cfg-card" style="margin-bottom:16px;">
        <div class="cfg-card-hdr">
          <div class="cfg-icon" style="background:#eaf2ff;">🏷️</div>
          <div>
            <div class="cfg-card-title">Metric names &amp; formulas</div>
            <div class="cfg-card-desc">Rename a KPI and describe how it's calculated — the glossary and metric cards update everywhere</div>
          </div>
        </div>
        <div class="cfg-card-body">
          <div style="display:grid; grid-template-columns: 220px 1fr; gap:10px; align-items:start;">
            <div class="cfg-field"><label>Metric</label>
              <select id="mr-key">
                <option value="gor">GOR (forecast card)</option>
                <option value="cr">CR (forecast card)</option>
                <option value="blr">BLR (forecast card)</option>
                <option value="mor">MOR (forecast card)</option>
                <option value="tor">TOR (forecast card)</option>
              </select>
            </div>
            <div style="display:grid; gap:8px;">
              <div class="cfg-field"><label>Display name (blank = built-in)</label><input id="mr-name" type="text" placeholder="e.g. Gross Guest Revenue"></div>
              <div class="cfg-field"><label>Formula (plain text, shown in the glossary)</label><input id="mr-formula" type="text" placeholder="e.g. sum of guest-paid totals across confirmed stays"></div>
              <div class="cfg-field"><label>Notes</label><input id="mr-notes" type="text" placeholder="shown as the card sub-label"></div>
              <div style="display:flex; gap:8px;">
                <button class="btn btn-primary btn-sm" id="mr-save">Save</button>
                <button class="btn btn-secondary btn-sm" id="mr-clear">Reset to built-in</button>
                <span id="mr-status" style="font-size:12px; color:var(--text-muted); align-self:center;"></span>
              </div>
            </div>
          </div>
          <div id="mr-list" style="margin-top:12px; font-size:12px; color:var(--text-muted);"></div>
        </div>
      </div>

      <!-- Row 3: Fee Types + blank / future -->
      <div class="cfg-card" style="margin-bottom:16px;">
        <div class="cfg-card-hdr">
          <div class="cfg-icon" style="background:#fdf4ff;">💰</div>
          <div>
            <div class="cfg-card-title">Fee Types</div>
            <div class="cfg-card-desc">Named fee categories with default treatment for revenue calculations</div>
          </div>
          <button class="btn btn-sm btn-outline" id="add-fee-type" style="margin-left:auto;">+ Add</button>
        </div>
        <div class="cfg-card-body" style="padding-top:0;">
          <div class="u-table-wrap">
            <table class="data-table">
              <thead><tr><th>Name</th><th>Treatment</th><th></th></tr></thead>
              <tbody id="fee-types-body">${this.renderFeeTypesTable()}</tbody>
            </table>
          </div>
        </div>
      </div>

      ${PageFooter.render([
        // R119: the plain words are the description; the short code stays the
        // no-wrap abbreviation (a long abbreviation ran over the next term)
        { abbr: 'Commissionable',         full: 'Owner\'s — commission charged',    note: 'the owner\'s money; WCH takes its % from it' },
        { abbr: 'Pass-through',           full: 'Company keeps',                   note: 'flows 100% to WCH (e.g. a cleaning fee the company keeps)' },
        { abbr: 'Bypass to Owner',        full: 'Owner\'s — NO commission',         note: 'the owner\'s money with no commission — the statement\'s Non-Commissionable Fees' },
        { abbr: 'Bypass to Jurisdiction', full: 'Tax',                             note: 'tax collected for government — untouched' },
        { abbr: 'Excluded',               full: 'Not counted',                     note: 'tracked but not counted (e.g. deposits)' },
      ])}

    </div>

    <!-- Formula modal -->
    <div id="formula-modal-overlay" class="cfg-modal-overlay">
      <div class="cfg-modal">
        <div class="cfg-modal-hdr">
          <h3 id="formula-modal-title">New Formula</h3>
          <button class="cfg-close" id="formula-modal-close">✕</button>
        </div>
        <div class="cfg-modal-body" id="formula-modal-body" style="max-height:70vh; overflow-y:auto;"></div>
        <div class="cfg-modal-footer" id="formula-modal-footer"></div>
      </div>
    </div>

    <!-- Fee type modal -->
    <div id="fee-type-modal-overlay" class="cfg-modal-overlay">
      <div class="cfg-modal" style="max-width:440px;">
        <div class="cfg-modal-hdr">
          <h3 id="fee-type-modal-title">New Fee Type</h3>
          <button class="cfg-close" id="fee-type-modal-close">✕</button>
        </div>
        <div class="cfg-modal-body" id="fee-type-modal-body"></div>
        <div class="cfg-modal-footer" id="fee-type-modal-footer"></div>
      </div>
    </div>

    ${this.grossHidden ? '' : `<!-- R118 assign / per-property facts modal (one overlay, two bodies); R119: it stacks above the statement formula builder -->
    <div id="grf-modal-overlay" class="cfg-modal-overlay" style="z-index:1100;">
      <div class="cfg-modal" style="max-width:560px;" role="dialog" aria-modal="true" aria-labelledby="grf-modal-title">
        <div class="cfg-modal-hdr">
          <h3 id="grf-modal-title">Statement formula</h3>
          <button class="cfg-close" data-grf="m-close" aria-label="Close">✕</button>
        </div>
        <div class="cfg-modal-body" id="grf-modal-body"></div>
        <div class="cfg-modal-footer" id="grf-modal-footer" style="flex-wrap:wrap;"></div>
      </div>
    </div>`}`;
  }

  // ── Formula table ─────────────────────────────────────────────────────────

  private renderFormulaTable(): string {
    if (!this.formulas.length) return `
      <div style="padding:40px; text-align:center; color:var(--text-muted);">
        <div style="font-size:32px; margin-bottom:10px;">📋</div>
        <div style="font-size:var(--fs-h); font-weight:500; margin-bottom:6px; color:var(--text);">No formulas yet</div>
        <div style="font-size:12px;">${this.canWriteFormulas ? 'Click + New Formula to create one.' : 'An admin or director creates them.'}</div>
      </div>`;

    return `
      <div class="u-table-wrap">
      <table class="data-table" style="min-width:680px;">
        <thead>
          <tr>
            <th style="width:28px;"></th>
            <th>Code</th>
            <th>Name</th>
            <th style="text-align:center;">Rate</th>
            <th style="text-align:center;">−Ch. Fees</th>
            <th class="num">Assigned</th>
            <th style="width:130px;"></th>
          </tr>
        </thead>
        <tbody id="formula-tbody">
          ${this.formulas.map(f => this.formulaRow(f)).join('')}
        </tbody>
      </table>
      </div>`;
  }

  private formulaRow(f: any): string {
    const exp = this.expandedFormulas.has(f.formula_code);
    const rate = (parseFloat(f.commission_rate) * 100).toFixed(1);
    const canDelete = (f.assignment_count || 0) === 0;
    return `
      <tr>
        <td style="padding:10px 4px 10px 12px; vertical-align:middle;">
          <button class="fml-expand-btn formula-expand-btn" data-code="${f.formula_code}" title="${exp ? 'Collapse' : 'Expand'}">${exp ? '▾' : '▸'}</button>
        </td>
        <td style="padding:10px 12px; vertical-align:middle;">
          <span class="fml-code">${Formatters.escapeHtml(f.formula_code)}</span>
        </td>
        <td class="u-truncate" style="padding:10px 12px; font-size:12px; vertical-align:middle; color:var(--text); max-width:240px;" title="${Formatters.escapeHtml(f.formula_name)}">
          ${Formatters.escapeHtml(f.formula_name)}
        </td>
        <td style="padding:10px 12px; text-align:center; vertical-align:middle;">
          <span class="fml-rate">${rate}%</span>
        </td>
        <td style="padding:10px 12px; text-align:center; vertical-align:middle; font-size:15px;">
          ${f.deduct_channel_fees_from_base
            ? '<span style="color:var(--success);">✓</span>'
            : '<span style="color:var(--text-muted);">—</span>'}
        </td>
        <td class="num" style="padding:10px 12px; vertical-align:middle; font-size:12px; color:${(f.assignment_count||0) > 0 ? 'var(--text)' : 'var(--text-muted)'};">
          ${f.assignment_count || 0} listing${(f.assignment_count||0) !== 1 ? 's' : ''}
        </td>
        <td style="padding:10px 12px; vertical-align:middle;">${this.canWriteFormulas ? `
          <button class="btn btn-sm btn-secondary formula-edit-btn" data-code="${f.formula_code}" style="margin-right:4px;">Edit</button>
          <button class="btn btn-sm btn-danger formula-delete-btn" data-code="${f.formula_code}"
            ${canDelete ? '' : 'disabled title="Remove assignments first"'}>Del</button>` : ''}
        </td>
      </tr>
      ${exp ? this.formulaDetailRow(f) : ''}`;
  }

  private formulaDetailRow(f: any): string {
    const treatments = f.treatments || {};
    const cats = [...new Set(LINE_ITEM_TYPES.map(t => t.category))];
    return `
      <tr class="fml-detail-row">
        <td colspan="7" style="padding:0 20px 20px 44px; background:var(--bg-subtle);">
          ${f.description ? `<p style="font-size:11px; color:var(--text-muted); margin:10px 0 12px; line-height:1.5;">${Formatters.escapeHtml(f.description)}</p>` : ''}
          <div class="fml-trt-grid">
            ${cats.map(cat => {
              const items = LINE_ITEM_TYPES.filter(t => t.category === cat);
              return `<div class="fml-trt-col">
                <div class="fml-cat-label">${cat}</div>
                ${items.map(t => {
                  const tr = treatments[t.code];
                  return `<div class="fml-trt-item">
                    <span class="fml-trt-name">${t.label}</span>
                    ${tr ? trtChip(tr) : '<span style="color:#d1d5db; font-size:10px;">—</span>'}
                  </div>`;
                }).join('')}
              </div>`;
            }).join('')}
          </div>
        </td>
      </tr>`;
  }

  // ── Formula form ──────────────────────────────────────────────────────────

  private renderFormulaFormBody(formula?: any): string {
    const t = formula?.treatments || {};
    const cats = [...new Set(LINE_ITEM_TYPES.map(x => x.category))];
    return `
      <div class="cfg-form-grid" style="margin-bottom:16px;">
        <div class="cfg-field">
          <label>Formula Code *</label>
          <input name="formula_code" value="${Formatters.escapeHtml(formula?.formula_code || '')}"
            ${formula ? 'readonly style="background:#f8fafc; color:var(--text-muted);"' : ''}
            placeholder="e.g. CUSTOM_20_BASE">
        </div>
        <div class="cfg-field">
          <label>Commission Rate *</label>
          <div style="display:flex; align-items:center; gap:8px;">
            <input name="commission_rate" type="number" step="0.1" min="0" max="100"
              value="${formula ? (parseFloat(formula.commission_rate) * 100).toFixed(1) : ''}"
              placeholder="20.0" style="width:90px; flex-shrink:0;">
            <span style="font-size:13px; color:var(--text-muted);">%</span>
          </div>
        </div>
        <div class="cfg-field cfg-field-full">
          <label>Formula Name *</label>
          <input name="formula_name" value="${Formatters.escapeHtml(formula?.formula_name || '')}"
            placeholder="e.g. WCH Standard — 20% on Base Rate Only">
        </div>
        <div class="cfg-field cfg-field-full">
          <label>Description</label>
          <textarea name="description" rows="2" placeholder="Optional notes about when to use this formula…">${Formatters.escapeHtml(formula?.description || '')}</textarea>
        </div>
      </div>
      <div style="display:flex; gap:24px; margin-bottom:20px;">
        <label class="cfg-checkbox-row">
          <input type="checkbox" name="deduct_channel_fees_from_base"
            ${formula?.deduct_channel_fees_from_base !== false ? 'checked' : ''}>
          Deduct channel fees from commission base
        </label>
        <label class="cfg-checkbox-row">
          <input type="checkbox" name="deduct_taxes_from_base"
            ${formula?.deduct_taxes_from_base !== false ? 'checked' : ''}>
          Deduct taxes from commission base
        </label>
      </div>
      <div class="cfg-field" style="margin-bottom:20px; width:200px;">
        <label>Minimum Fee per Booking ($)</label>
        <input name="minimum_fee_per_booking" type="number" step="0.01" min="0"
          value="${formula?.minimum_fee_per_booking || ''}" placeholder="Leave blank for none">
      </div>
      <hr class="cfg-divider">
      <div class="cfg-section-label">Line Item Treatments</div>
      <p id="formula-what" style="font-size:12px; color:var(--text); margin:-6px 0 8px; line-height:1.5;"><strong>Commission (P&amp;L) formula:</strong> how the company books each reservation in its own P&amp;L (management rate, what is commissionable, whose cleaning it is). Owners' Gross Revenue is not built here: it is a column of the statement formulas (Formulas &amp; Fees → Statement formulas).</p>
      <p id="formula-trt-legend" style="font-size:11.5px; color:var(--text-muted); margin:0 0 12px; line-height:1.5;"><strong>In the company P&amp;L:</strong> <strong>Owner's — commission charged</strong> = the owner's money; WCH takes its %. <strong>Owner's — NO commission</strong> = the owner's money with no commission. <strong>Company keeps</strong> = WCH's money. On the owner portal a guest fee counts as the owner's only when it's marked as the owner's here (the Resort Fee always does, unless it's Not counted or Tax), and the property's statement formula then decides where an owner's fee lands — in Gross as Resolutions/Fees, as Non-Commissionable Fees, or kept by the company — so these can read differently there; a statement formula's Preview shows a property's exact numbers. This formula does reach the owner portal: <strong>every property on it whose statement formula is <em>Automatic</em></strong> (the built-in default, or any statement formula on the Automatic preset) takes cleaning placement, the channel-fee base, the commission base and the rate from here. On any other statement formula an Automatic cleaning or commission-base column follows it too, and so does the rate unless that formula fixes one.${REV_FACTS} <strong>On every property on it</strong>, ${REV_TRT_TXT}<span id="formula-auto-count">${formula && this.gross ? ' Checking how many of its properties that is…' : ''}</span></p>
      <div class="cfg-trt-grid">
        ${cats.map(cat => `
          <div class="cfg-trt-col">
            <div class="fml-cat-label" style="margin-bottom:8px;">${cat}</div>
            ${LINE_ITEM_TYPES.filter(x => x.category === cat).map(x => `
              <div class="cfg-trt-row">
                <span class="cfg-trt-lbl">${x.label}</span>
                ${trtSelect(`treatments[${x.code}]`, t[x.code] || '')}
              </div>`).join('')}
          </div>`).join('')}
      </div>`;
  }

  // ── Fee types ─────────────────────────────────────────────────────────────

  private renderFeeTypesTable(): string {
    if (!this.feeTypes.length) return `
      <tr><td colspan="3" style="text-align:center; padding:24px; color:var(--text-muted); font-size:12px;">
        No fee types defined yet.
      </td></tr>`;
    const labels: Record<string, string> = {
      commission:   '<span class="trt trt-comm">Commission</span>',
      pass_through: '<span class="trt trt-pass">Pass-through</span>',
      keep_100:     '<span class="trt trt-pass">Keep 100%</span>',
      excluded:     '<span class="trt trt-excl">Excluded</span>',
    };
    return this.feeTypes.map(ft => `
      <tr>
        <td style="font-size:12px;">
          <span style="font-weight:500;">${Formatters.escapeHtml(ft.name)}</span>
          ${ft.description ? `<div style="font-size:11px; color:var(--text-muted); margin-top:1px;">${ft.description}</div>` : ''}
        </td>
        <td>${labels[ft.default_treatment] || ft.default_treatment}</td>
        <td style="text-align:right;">
          <button class="btn btn-sm btn-secondary edit-fee-type" data-id="${ft.id}">Edit</button>
          <button class="btn btn-sm btn-outline delete-fee-type" data-id="${ft.id}" style="margin-left:4px;">✕</button>
        </td>
      </tr>`).join('');
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  private async loadFormulas(): Promise<void> {
    try {
      const res = await fetch('/api/formulas');
      const list = res.ok ? await res.json() : [];
      // R119: the detail read carries the treatments but not assignment_count —
      // merge it over the list row (it replaced it: every formula read
      // "0 listings", Del was never disabled, the edit confirm had no count)
      this.formulas = await Promise.all(list.map(async (f: any) => {
        try {
          const r = await fetch(`/api/formulas/${encodeURIComponent(f.formula_code)}`);
          return r.ok ? { ...f, ...(await r.json()), assignment_count: f.assignment_count } : f;
        } catch { return f; }
      }));
    } catch { this.formulas = []; }
  }

  private async loadSettings(): Promise<void> {
    try {
      const res = await fetch('/api/settings');
      const d = res.ok ? await res.json() : {};
      this.settings = {
        default_mgmt_fee_percent: parseFloat(d.default_mgmt_fee_percent) || 20,
        default_expense_ratio_percent: parseFloat(d.default_expense_ratio_percent) || 72,
        admin_contracts_url: d.admin_contracts_url || '',
      };
    } catch { this.settings = { default_mgmt_fee_percent: 20, default_expense_ratio_percent: 72, admin_contracts_url: '' }; }
  }

  private async loadFeeTypes(): Promise<void> {
    try {
      const res = await fetch('/api/fee-types');
      this.feeTypes = res.ok ? await res.json() : [];
    } catch { this.feeTypes = []; }
  }

  private async loadStats(): Promise<void> {
    try {
      const [{ count: l }, { count: r }] = await Promise.all([
        supabase.from('listings').select('*', { count: 'exact', head: true }),
        supabase.from('reservations').select('*', { count: 'exact', head: true }),
      ]);
      const el1 = document.getElementById('db-listings-count');
      const el2 = document.getElementById('db-reservations-count');
      if (el1) el1.textContent = String(l || 0);
      if (el2) el2.textContent = String(r || 0);
    } catch { /* ignore */ }
  }

  // ── Event binding ─────────────────────────────────────────────────────────

  private async bindMetricRegistry(): Promise<void> {
    const sel = document.getElementById('mr-key') as HTMLSelectElement | null;
    if (!sel) return;
    // Every glossary definition is editable too.
    try {
      const { metricsByCategory } = await import('../shared/lib/metricDefinitions');
      const byCat = metricsByCategory();
      for (const [cat, defs] of Object.entries(byCat)) {
        const og = document.createElement('optgroup');
        og.label = cat;
        for (const d of defs as any[]) {
          const o = document.createElement('option');
          o.value = d.key; o.textContent = d.term;
          og.appendChild(o);
        }
        sel.appendChild(og);
      }
    } catch (_) { /* glossary import optional */ }
    let registry: Record<string, any> = {};
    const refresh = async () => {
      try { const r = await fetch('/api/metric-registry'); registry = r.ok ? await r.json() : {}; } catch (_) { registry = {}; }
      const list = document.getElementById('mr-list');
      if (list) {
        const keys = Object.keys(registry);
        list.innerHTML = keys.length
          ? 'Custom: ' + keys.map(k => `<span class="mono" style="margin-right:10px;">${k} → ${registry[k].display_name || '(formula/notes only)'}</span>`).join('')
          : 'No custom metric names yet — everything shows its built-in name.';
      }
      fill();
    };
    const fill = () => {
      const e = registry[sel.value] || {};
      (document.getElementById('mr-name') as HTMLInputElement).value = e.display_name || '';
      (document.getElementById('mr-formula') as HTMLInputElement).value = e.formula_text || '';
      (document.getElementById('mr-notes') as HTMLInputElement).value = e.notes || '';
    };
    sel.addEventListener('change', fill);
    const status = (t: string) => { const s = document.getElementById('mr-status'); if (s) { s.textContent = t; setTimeout(() => { s.textContent = ''; }, 2500); } };
    document.getElementById('mr-save')?.addEventListener('click', async () => {
      await fetch('/api/metric-registry', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
        key: sel.value,
        display_name: (document.getElementById('mr-name') as HTMLInputElement).value.trim(),
        formula_text: (document.getElementById('mr-formula') as HTMLInputElement).value.trim(),
        notes: (document.getElementById('mr-notes') as HTMLInputElement).value.trim(),
      }) });
      status('Saved — glossary + metric cards updated.');
      await refresh();
    });
    document.getElementById('mr-clear')?.addEventListener('click', async () => {
      await fetch('/api/metric-registry', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key: sel.value }) });
      status('Reset to built-in.');
      await refresh();
    });
    await refresh();
  }

  private bindAll(): void {
    this.bindMetricRegistry();
    this.bindSettings();
    this.bindFormulas();
    this.bindFeeTypes();
    this.bindGross();   // R118
    document.getElementById('goto-property-settings')?.addEventListener('click', (e) => {
      e.preventDefault();
      sessionStorage.setItem('hub_tab', 'settings');
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'portfolio' }));
    });
  }

  private bindSettings(): void {
    const saveSetting = async (inputId: string, key: string) => {
      const val = (document.getElementById(inputId) as HTMLInputElement)?.value;
      const res = await fetch('/api/settings', { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ [key]: val }) });
      if (!res.ok) alert('Failed to save.');
    };
    document.getElementById('save-mgmt-fee')?.addEventListener('click', () => saveSetting('default-mgmt-fee', 'default_mgmt_fee_percent'));
    document.getElementById('save-expense-ratio')?.addEventListener('click', () => saveSetting('default-expense-ratio', 'default_expense_ratio_percent'));
    document.getElementById('save-contracts-url')?.addEventListener('click', () => saveSetting('admin-contracts-url', 'admin_contracts_url'));

    const syncBtn = async (btnId: string, url: string, label: string, done: string) => {
      const btn = document.getElementById(btnId) as HTMLButtonElement | null;
      if (!btn) return;
      btn.addEventListener('click', async () => {
        btn.disabled = true; btn.textContent = 'Syncing…';
        try {
          const r = await fetch(url);
          const d = await r.json();
          alert(`${done}: ${d.synced || 0} ${label}`);
          await this.loadStats();
        } catch { alert('Sync failed.'); }
        finally { btn.disabled = false; btn.textContent = 'Sync'; }
      });
    };
    syncBtn('sync-hostaway-btn', '/api/sync/listings', 'listings synced', 'Sync complete');
    // R125: a full reservations sync runs for minutes — start it in the background and follow its status
    const resBtn = document.getElementById('sync-reservations-btn') as HTMLButtonElement | null;
    resBtn?.addEventListener('click', async () => {
      resBtn.disabled = true; resBtn.textContent = 'Syncing…';
      try {
        const r = await fetch('/api/sync/reservations?background=1');
        const d = await r.json();
        if (!r.ok || !d.run_id) throw new Error(d.error || `HTTP ${r.status}`);
        let s: { state?: string; error?: string; result?: { synced?: number } } | null = null;
        const deadline = Date.now() + 30 * 60 * 1000;
        while (Date.now() < deadline) {
          await new Promise(res => setTimeout(res, 5000));
          const sr = await fetch(`/api/sync/reservations/status?run=${encodeURIComponent(d.run_id)}`);
          if (sr.status === 404) { s = await sr.json(); break; }
          if (!sr.ok) continue;
          s = await sr.json();
          if (s && (s.state === 'ok' || s.state === 'failed')) break;
        }
        if (s && s.state === 'ok') alert(`Sync complete: ${s.result?.synced || 0} reservations synced`);
        else alert(`Sync failed: ${(s && s.error) || 'still running after 30 minutes'}`);
        await this.loadStats();
      } catch { alert('Sync failed.'); }
      finally { resBtn.disabled = false; resBtn.textContent = 'Sync'; }
    });
  }

  private bindFormulas(): void {
    // R118: expand / Edit / Del are delegated from the STABLE container.
    // refreshFormulaTable() swaps #formula-library-content's innerHTML, so the
    // old listeners on #formula-tbody died with the first expand or save (and
    // an initially empty library had no tbody to bind at all) — every Edit/Del
    // then did nothing until a full page reload.
    document.getElementById('formula-library-content')?.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      const exp  = target.closest('.formula-expand-btn') as HTMLElement | null;
      const edit = target.closest('.formula-edit-btn') as HTMLElement | null;
      const del  = target.closest('.formula-delete-btn') as HTMLButtonElement | null;
      if (exp) {
        const code = exp.dataset.code!;
        this.expandedFormulas.has(code) ? this.expandedFormulas.delete(code) : this.expandedFormulas.add(code);
        this.refreshFormulaTable();
        return;
      }
      if (edit) {
        const f = this.formulas.find(x => x.formula_code === edit.dataset.code);
        if (f) this.openFormulaModal(f);
        return;
      }
      if (del && !del.disabled && this.canWriteFormulas) {
        if (!confirm(`Delete formula "${del.dataset.code}"?`)) return;
        try {
          const res = await fetch(`/api/formulas/${encodeURIComponent(del.dataset.code!)}`, { method: 'DELETE' });
          if (res.ok) await this.reloadFormulas();
          else { const d = await res.json().catch(() => ({})); alert(d.error || 'Failed to delete.'); }
        } catch { alert('Could not reach the server — the formula was not deleted.'); }
      }
    });

    // New formula
    document.getElementById('new-formula-btn')?.addEventListener('click', () => this.openFormulaModal());

    // Modal close
    const closeFormula = () => {
      document.getElementById('formula-modal-overlay')!.classList.remove('open');
    };
    document.getElementById('formula-modal-close')?.addEventListener('click', closeFormula);
    document.getElementById('formula-modal-overlay')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'formula-modal-overlay') closeFormula();
    });
  }

  // R119 review #16: a commission (revenue) formula reaches the OWNER PORTAL too, not only the
  // P&L (lib/ownerFinance resolveColumns): every property it is assigned to whose statement
  // formula is on the Automatic preset (the built-in default, or any 'auto' formula) takes its
  // cleaning placement, channel-fee base, commission base and rate from it; on another statement
  // formula an Automatic cleaning / commission-base column follows it, and the rate unless the
  // statement formula fixes one or the property's facts hold their own.
  // Counted from GET /api/formula-assignments (each property's commission formula) + the
  // owner-finance store (assign → default_rule, as lib/ownerFinance ruleFor; each formula's
  // server-derived preset). null = can't tell (not admin / director, or a read failed).
  private async revenueFormulaReach(code: string): Promise<RevReach | null> {
    const g = this.gross;
    if (!g || !code) return null;
    let rows: any[];
    try {
      const r = await fetch('/api/formula-assignments');
      const j = r.ok ? await r.json().catch(() => null) : null;
      if (!Array.isArray(j)) return null;
      rows = j;
    } catch { return null; }
    const rules: any[] = Array.isArray(g.rules) ? g.rules : [];
    const byId = new Map(rules.map((r: any) => [String(r.id), r]));
    const presets = this.grossPresets();
    let used = 0, auto = 0, part = 0, rate = 0;
    for (const row of rows) {
      const a = row && row.assignment;
      if (!a || a.effective_to || String(a.formula_code) !== code) continue;
      const lid = String(row.listing_id ?? row.pms_listing_id ?? '');
      used++;
      // lib/ownerFinance ruleFor: the assigned statement formula, else the default; an unknown id → the first rule
      const rule = byId.get(String((g.assign && g.assign[lid]) || g.default_rule || GRF_BUILTIN)) || rules[0] || null;
      const cols = rule ? formulaColumns(rule) : null;
      const preset = !rule || !cols ? 'auto' : (PRESET_IDS as readonly string[]).includes(String(rule.preset)) ? String(rule.preset) : presetOf(cols, presets);
      if (preset === 'auto' || !cols) { auto++; continue; }
      // resolveColumns: an automatic cleaning (legacy "never in Gross" too) / commission base follows the revenue formula
      if (cols.cleaning === 'auto' || cols.cleaning === 'auto_not_gross' || cols.commission_base === 'auto') { part++; continue; }
      const prof = (g.profiles || {})[lid];
      if (cols.commission_rate == null && !(prof && grfNum(prof.commission_rate) != null)) rate++;
    }
    return { used, auto, part, rate };
  }

  private openFormulaModal(formula?: any): void {
    if (!this.canWriteFormulas) return;   // R118 review #15: the server refuses every other role
    const overlay = document.getElementById('formula-modal-overlay')!;
    const title   = document.getElementById('formula-modal-title')!;
    const body    = document.getElementById('formula-modal-body')!;
    const footer  = document.getElementById('formula-modal-footer')!;
    const isEdit  = !!formula;

    title.textContent = formula ? `Edit · ${formula.formula_code}` : 'New Formula';
    body.innerHTML = this.renderFormulaFormBody(formula);
    // R118: an inline error line — a refused save says why, in the modal.
    footer.innerHTML = `
      <span id="formula-modal-err" role="alert" style="display:none; margin-right:auto; align-self:center; font-size:12.5px; line-height:1.4; color:var(--danger-text,#b91c1c);"></span>
      <button class="btn btn-secondary" id="formula-cancel">Cancel</button>
      <button class="btn btn-primary" id="formula-submit">${formula ? 'Save Changes' : 'Create Formula'}</button>`;

    overlay.classList.add('open');

    // R119 review #16: how many of this formula's properties are on an Automatic statement
    // formula (their owner portal follows it too) — read fresh for every edit; Save uses the
    // answer once it is in (never waits on it)
    let reach: RevReach | null = null;
    if (isEdit && this.gross) void this.revenueFormulaReach(String(formula.formula_code)).then((n) => {
      reach = n;
      const el = body.querySelector<HTMLElement>('#formula-auto-count');
      if (el && overlay.classList.contains('open')) el.textContent = revReachLine(n);
    });

    document.getElementById('formula-cancel')?.addEventListener('click', () => overlay.classList.remove('open'));

    const submit  = document.getElementById('formula-submit') as HTMLButtonElement;
    const errLine = document.getElementById('formula-modal-err')!;
    const showErr = (msg: string, field?: string) => {
      errLine.textContent = msg;
      errLine.style.display = msg ? '' : 'none';
      if (field) (body.querySelector(`[name="${field}"]`) as HTMLElement | null)?.focus();
    };

    // R118 (Jesse: "No matter what I do, when I click on 'Create Formula'
    // nothing happens."): the handler opened with `new FormData(<div>)` — the
    // modal has no <form>, so it threw before any request and the async
    // handler swallowed it. Gone; the fields were always gathered by hand below.
    submit.addEventListener('click', async () => {
      if (this.formulaSaving) { showErr('Still saving the last change — one moment.'); return; }
      const data: Record<string, any> = {};
      body.querySelectorAll('[name]').forEach((el: any) => {
        const n = el.name as string;
        if (el.type === 'checkbox') data[n] = el.checked;
        else if (n.startsWith('treatments[')) {
          if (!data.treatments) data.treatments = {};
          const code = n.slice('treatments['.length, -1);
          if (el.value) data.treatments[code] = el.value;
        } else {
          data[n] = el.value;
        }
      });

      const ratePct = parseFloat(String(data.commission_rate ?? '').trim());
      const minFee  = String(data.minimum_fee_per_booking ?? '').trim();
      const payload: Record<string, any> = {
        formula_code:  isEdit ? formula.formula_code : String(data.formula_code || '').trim().toUpperCase(),
        formula_name:  String(data.formula_name || '').trim(),
        description:   String(data.description || '').trim() || null,
        commission_rate: Number((ratePct / 100).toFixed(6)),   // the % field → the stored fraction (20 → 0.2)
        deduct_channel_fees_from_base: !!data.deduct_channel_fees_from_base,
        deduct_taxes_from_base: !!data.deduct_taxes_from_base,
        minimum_fee_per_booking: minFee === '' ? null : Number(minFee),
        // R118: an edit keeps the formula's own start date (this was hard-coded
        // '2023-01-01' and overwrote it on every save); absent → the server keeps it.
        effective_from: isEdit ? (formula.effective_from ? String(formula.effective_from).slice(0, 10) : undefined) : '2023-01-01',
        treatments: data.treatments || {},
      };
      const bad = formulaInputError(payload, ratePct, isEdit);
      if (bad) { showErr(bad.msg, bad.field); return; }
      showErr('');
      // R119: an edit reaches every property on the formula — today's
      // WCH_INCLUDE_CLEAN_20 edit (cleaning → "Bypass to Owner") moved 20 at
      // once. Say how many first; Cancel sends nothing.
      const used = isEdit ? Number(formula.assignment_count) || 0 : 0;
      // R119 review #16: …and which owner portals move with it (an Automatic statement formula
      // takes cleaning placement, fee base, commission base and rate from here; others its rate)
      // R119 fix-verify (#16): a $0 minimum lifts no fee (reservationMoney), so it isn't named;
      // `used` lets the reach sentence say "all N" only when its count is this one
      if (used > 1 && !confirm(`Save “${payload.formula_name}”? This commission formula is used by ${used} properties — the fee split changes for all ${used}.${revReachConfirm(reach, Number(payload.minimum_fee_per_booking) > 0 ? true : Number(formula && formula.minimum_fee_per_booking) > 0 ? 'cleared' : false, used)}`)) return;

      const url = isEdit ? `/api/formulas/${encodeURIComponent(formula.formula_code)}` : '/api/formulas';
      const label = submit.textContent;
      this.formulaSaving = true;
      submit.disabled = true;           // a double-click sent two POSTs (the second hit "duplicate key")
      submit.textContent = 'Saving…';
      let saved = false;
      try {
        const res = await fetch(url, { method: isEdit ? 'PUT' : 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload) });
        const d = await res.json().catch(() => ({}));
        if (res.ok) saved = true;
        else showErr(d.error || `Could not save the formula (HTTP ${res.status}).`);
      } catch {
        showErr('Could not reach the server — nothing was saved. Check the connection and try again.');
      } finally {
        this.formulaSaving = false;
        submit.disabled = false;
        submit.textContent = label;
      }
      if (saved) { overlay.classList.remove('open'); await this.reloadFormulas(); }
    });
  }

  private static FEE_TREATMENTS: { value: string; label: string }[] = [
    { value: 'commission',   label: 'Commission (commissionable)' },
    { value: 'pass_through', label: 'Pass-through' },
    { value: 'keep_100',     label: 'Keep 100%' },
    { value: 'excluded',     label: 'Excluded' },
  ];

  private bindFeeTypes(): void {
    document.getElementById('add-fee-type')?.addEventListener('click', () => this.openFeeTypeModal(null));

    const tbody = document.getElementById('fee-types-body');
    tbody?.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      const edit = target.closest('.edit-fee-type') as HTMLElement | null;
      const del  = target.closest('.delete-fee-type') as HTMLElement | null;
      if (edit) {
        const ft = this.feeTypes.find(f => f.id === edit.dataset.id);
        if (ft) this.openFeeTypeModal(ft);
      }
      if (del) {
        if (!confirm('Delete this fee type?')) return;
        const res = await fetch(`/api/fee-types/${del.dataset.id}`, { method: 'DELETE' });
        if (res.ok) { this.feeTypes = this.feeTypes.filter(f => f.id !== del.dataset.id); this.refreshFeeTypes(); }
        else alert('Failed to delete.');
      }
    });
  }

  // Add/edit a fee type via the styled modal (replaces native prompt()).
  private openFeeTypeModal(ft: any | null): void {
    const overlay = document.getElementById('fee-type-modal-overlay')!;
    const title   = document.getElementById('fee-type-modal-title')!;
    const body    = document.getElementById('fee-type-modal-body')!;
    const footer  = document.getElementById('fee-type-modal-footer')!;
    const isEdit  = !!ft;

    title.textContent = isEdit ? 'Edit Fee Type' : 'New Fee Type';
    const options = SettingsPage.FEE_TREATMENTS.map(t =>
      `<option value="${t.value}"${ft && ft.default_treatment === t.value ? ' selected' : ''}>${t.label}</option>`
    ).join('');
    body.innerHTML = `
      <div class="form-group" style="margin-bottom:14px;">
        <label style="display:block; font-size:12px; font-weight:600; color:var(--text-muted); margin-bottom:6px;">Fee type name</label>
        <input type="text" name="name" class="input" placeholder="e.g. Cleaning Fee" value="${Formatters.escapeHtml(ft?.name || '')}"
          style="width:100%; padding:8px 12px; border:1px solid var(--border); border-radius:6px; font-size:14px;">
      </div>
      <div class="form-group">
        <label style="display:block; font-size:12px; font-weight:600; color:var(--text-muted); margin-bottom:6px;">Default treatment</label>
        <select name="default_treatment" class="input" style="width:100%; padding:8px 12px; border:1px solid var(--border); border-radius:6px; font-size:14px; background:white;">
          ${options}
        </select>
        <div style="font-size:11px; color:var(--text-muted); margin-top:6px; line-height:1.45;">How this fee is handled in revenue math: <strong>Commission</strong> counts toward commissionable revenue, <strong>Pass-through</strong> flows to the owner, <strong>Keep 100%</strong> stays with management, <strong>Excluded</strong> is ignored.</div>
      </div>`;
    footer.innerHTML = `
      <button class="btn btn-secondary" id="fee-type-cancel">Cancel</button>
      <button class="btn btn-primary" id="fee-type-submit">${isEdit ? 'Save Changes' : 'Add Fee Type'}</button>`;

    overlay.classList.add('open');
    const close = () => overlay.classList.remove('open');
    document.getElementById('fee-type-cancel')?.addEventListener('click', close);
    document.getElementById('fee-type-modal-close')?.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if ((e.target as HTMLElement).id === 'fee-type-modal-overlay') close(); });

    document.getElementById('fee-type-submit')?.addEventListener('click', async () => {
      const name = (body.querySelector('[name="name"]') as HTMLInputElement)?.value.trim();
      const default_treatment = (body.querySelector('[name="default_treatment"]') as HTMLSelectElement)?.value;
      if (!name) { alert('Please enter a fee type name.'); return; }
      const url = isEdit ? `/api/fee-types/${ft.id}` : '/api/fee-types';
      const method = isEdit ? 'PUT' : 'POST';
      const payload = isEdit
        ? { name, default_treatment, description: ft.description }
        : { name, default_treatment };
      const res = await fetch(url, { method, headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload) });
      if (res.ok) {
        const saved = await res.json();
        if (isEdit) { const i = this.feeTypes.findIndex(f => f.id === ft.id); if (i !== -1) this.feeTypes[i] = saved; }
        else this.feeTypes.push(saved);
        close();
        this.refreshFeeTypes();
      } else { const d = await res.json().catch(() => ({})); alert(d.error || 'Failed to save fee type.'); }
    });
  }

  // ── Partial re-renders ────────────────────────────────────────────────────

  private async reloadFormulas(): Promise<void> {
    await this.loadFormulas();
    this.refreshFormulaTable();
    // assignments UI moved to Property Settings — nothing to refresh here
  }

  private refreshFormulaTable(): void {
    const el = document.getElementById('formula-library-content');
    if (el) el.innerHTML = this.renderFormulaTable();
  }

  private refreshFeeTypes(): void {
    const el = document.getElementById('fee-types-body');
    if (el) el.innerHTML = this.renderFeeTypesTable();
  }

  // ══ R118 gross revenue formulas ═══════════════════════════════════════════
  // A gross formula (the store calls it a rule) = what counts toward the Gross
  // Revenue an owner sees: rent always; cleaning, owner fees, company fees,
  // taxes each on/off; before or after the owner's channel fee. Each modal
  // save PUTs the store with its version (409 → reload + say so). A rule save
  // never sends `assign` / `profiles`: those have their own patch endpoints
  // (POST /assign, /profile), so a stale copy here can't undo a property
  // assignment made meanwhile. "Match the statements" learns each property's
  // own settings (company fee per booking, tax %, cleaning, commission rate +
  // base) from the published statements — dry run first, apply on confirm.

  // R119: the R118 rule modal is gone — a formula is now the whole statement
  // ladder, edited in the StatementBuilder overlay (one POST /formulas per
  // save, no whole-store version) with a live preview against a property's
  // statement. The rest of the section (assign picker, channel rates, Match the
  // statements, Compare a month, property facts) stays here.

  // R37 contract (HubPage delegates the 15-min auto-refresh veto here): an open
  // modal (any of this page's), the statement formula builder (R119), unsaved
  // channel rates, or a dry run waiting for "Apply" survives the timer.
  suspendAutoRefresh(): boolean {
    if (this.container.querySelector('.cfg-modal-overlay.open')) return true;
    if (this.sb && this.sb.isOpen()) return true;
    return !!this.grossRatesDraft || !!(this.grossCalib && this.grossCalib.dry_run === true && !this.grossCalib.error);
  }

  // ── R119: the statement formula builder ───────────────────────────────────
  private builder(): StatementBuilder {
    if (!this.sb) this.sb = new StatementBuilder(this.container, {
      store: () => this.gross,
      calib: () => this.grossCalib,
      adoptStore: (j: any) => { this.adoptGrossStore(j); this.drawGross(); },
      openAssign: (id: string) => this.openGrossAssignModal(this.grossRule(id)),
      editFacts: (lid: string) => this.openGrossProfileModal(lid),
      factsSaved: (lid: string) => { void this.builderFactsSaved(lid); },
      saved: (f: any, isNew: boolean, name: string) => { void this.builderSaved(f, isNew, name); },
      closed: () => this.drawGross(),
    });
    return this.sb;
  }
  private openBuilder(o: { formula?: any; copy?: boolean; mode?: 'edit' | 'preview'; listingId?: string; month?: string; learn?: boolean }): void {
    if (!this.gross) return;
    this.builder().open(o);
  }
  // a save landed (the store it stamped is adopted): close, re-read, and a NEW
  // formula goes straight to "which properties use it?" (the picker patch)
  private async builderSaved(f: any, isNew: boolean, name: string): Promise<void> {
    this.sb?.close(true);
    await this.loadGross();   // fresh version + listings (the adopted store stands if this read fails)
    this.setGrossNote(isNew
      ? `✓ Saved “${name}” — now tick the properties that use it (it is also in every property's Statement picker in Portfolio → Property Settings → Formulas).`
      : `✓ Saved “${name}”.`);
    this.drawGross();
    if (isNew) this.openGrossAssignModal(this.grossRule(String(f && f.id)) || f);
  }
  // R121 review #24: the builder wrote a property's facts ("Save facts": a hand-set profile; Learn's
  // Assign: learned facts + a formula) — a pending dry run's row for it promised what Apply WOULD do
  // with the old profile. The same as the facts modal (R118 review #11): its current facts follow,
  // and the row is re-checked by the server (a failed check says "run the check again").
  private async builderFactsSaved(lid: string): Promise<void> {
    const c = this.grossCalib;
    const row = ((c && Array.isArray(c.listings) && c.listings) || []).find((x: any) => String(x.listing_id) === String(lid));
    if (!row) return;
    const saved = ((this.gross && this.gross.profiles) || {})[lid];
    row.current = saved && typeof saved === 'object' ? saved : null;
    await this.grossRecheckRow(String(lid));
    this.drawGross();
  }
  // R119: Property Settings' "Preview · Learn" hand ONE property over (one-shot
  // sessionStorage 'sf_focus' = { listing_id, month, learn? }) → the builder
  // opens on the formula that property uses, in preview mode.
  private takeStatementFocus(): void {
    let raw = '';
    try { raw = sessionStorage.getItem('sf_focus') || ''; if (raw) sessionStorage.removeItem('sf_focus'); } catch { return; }
    if (!raw || !this.gross) return;   // a 403 (not admin / director) just drops it
    let f: any = null;
    try { f = JSON.parse(raw); } catch { return; }
    const lid = f && f.listing_id != null ? String(f.listing_id) : '';
    if (!lid) return;
    const rule = this.grossRule(this.gross.assign[lid] || this.gross.default_rule) || this.grossRule(GRF_BUILTIN);
    if (!rule) return;
    // R121: Property Settings' "Edit" opens the builder on the Ladder (the formula's columns), Preview / Learn on the preview
    this.openBuilder({ formula: rule, mode: f.mode === 'edit' ? 'edit' : 'preview', listingId: lid, month: typeof f.month === 'string' ? f.month : undefined, learn: f.learn === true });
  }
  // R121: Property Settings' "Edit" beside the Commission (P&L) select (one-shot sessionStorage 'cf_focus') —
  // the commission formula's own modal (read-only for a login that can't edit formulas: the modal says so)
  private takeCommissionFocus(): void {
    let code = '';
    try { code = sessionStorage.getItem('cf_focus') || ''; if (code) sessionStorage.removeItem('cf_focus'); } catch { return; }
    if (!code) return;
    const f = this.formulas.find(x => x.formula_code === code);
    if (f) this.openFormulaModal(f);
  }
  private grossPresets(): Preset[] {
    const p = this.gross && this.gross.presets;
    return Array.isArray(p) && p.length ? p : [...PRESETS_FALLBACK];
  }

  private async loadGross(): Promise<boolean> {
    try {
      const r = await fetch('/api/owner-finance/rules');
      if (r.status === 401 || r.status === 403) { this.grossHidden = true; this.gross = null; return false; }   // money is admin+director only
      const j = await r.json().catch(() => null);
      if (!r.ok || !j || !Array.isArray(j.rules)) throw new Error((j && j.error) || `Couldn't load the statement formulas (HTTP ${r.status}).`);
      this.gross = { ...j, assign: j.assign || {}, profiles: j.profiles || {}, channel_rates: j.channel_rates || {}, listings: Array.isArray(j.listings) ? j.listings : [] };
      this.grossErr = '';
      return true;
    } catch (e: any) { this.grossErr = (e && e.message) || "Couldn't load the statement formulas."; return false; }
  }

  private grossRule(id: string): any { return ((this.gross && this.gross.rules) || []).find((r: any) => r.id === id) || null; }
  private grossRuleName(id: string): string { const r = this.grossRule(id); return r ? String(r.name) : id; }
  private grossListingName(id: string): string { const l = ((this.gross && this.gross.listings) || []).find((x: any) => String(x.id) === String(id)); return l ? String(l.name || id) : String(id); }
  // properties resolving to a rule (explicit + the default's unassigned ones)
  private grossCount(id: string): { all: number; explicit: number } {
    const s = this.gross; let all = 0, explicit = 0;
    for (const l of (s.listings || [])) { const a = s.assign[String(l.id)]; if ((a || s.default_rule) === id) all++; if (a === id) explicit++; }
    return { all, explicit };
  }
  private setGrossNote(msg: string, bad = false): void { this.grossNote = msg; this.grossNoteBad = bad; }
  private drawGross(): void {
    const root = this.container.querySelector<HTMLElement>('#grf-root');
    if (root) root.innerHTML = this.grossBodyHtml();
  }

  private grossSectionHtml(): string {
    if (this.grossHidden) return '';
    return `
      <div class="pf-section grf" id="grf-section" style="margin-bottom:16px;">
        <style>
          .grf .pf-section-hdr { gap:12px; flex-wrap:wrap; }
          .grf .pf-section-hdr > div { flex:1 1 260px; min-width:0; }
          .grf .pf-section-hdr > .grf-bar { flex:0 1 auto; justify-content:flex-end; }
          .grf-note { padding:10px 20px; font-size:12.5px; border-bottom:1px solid var(--border-soft); color:var(--success-text,#166534); }
          .grf-note.bad { color:var(--danger-text,#991b1b); }
          .grf-chips { display:flex; flex-wrap:wrap; gap:4px; max-width:420px; }
          .grf-chip { display:inline-flex; padding:2px 8px; border-radius:var(--radius-pill); font-size:11px; background:var(--bg-subtle); border:1px solid var(--border); color:var(--text); white-space:nowrap; }
          .grf-badges { display:flex; gap:4px; flex-wrap:wrap; margin-top:3px; }
          .grf-legacy-pill { background:var(--warning-soft); color:var(--warning-text); }
          .grf-tbl td.grf-name { white-space:normal; min-width:180px; max-width:280px; }
          .grf-acts { display:flex; flex-wrap:wrap; gap:4px; justify-content:flex-end; min-width:250px; }
          .grf-tick { width:16px; height:16px; accent-color:var(--accent); }
          .grf-legacy { margin:4px 0 12px; border:1px solid var(--border-soft); border-radius:8px; padding:8px 10px; }
          .grf-explain { margin-top:6px; font-size:12.5px; color:var(--text-muted); max-width:820px; }
          .grf-explain summary { cursor:pointer; color:var(--accent); font-weight:600; min-height:30px; display:inline-flex; align-items:center; }
          .grf-explain p { margin:4px 0; line-height:1.5; }
          .grf-legacy summary { font-size:12px; font-weight:600; color:var(--text-muted); cursor:pointer; }
          .grf-sf b { font-weight:600; }
          .grf-meta { padding:10px 20px; font-size:11.5px; color:var(--text-muted); border-top:1px solid var(--border-soft); }
          .grf-meta a { color:var(--info); }
          .grf-sub { border-top:1px solid var(--border); padding:14px 20px 16px; min-width:0; }
          .grf-sub h4 { font-size:13px; font-weight:600; margin:0; color:var(--text); }
          .grf-sub > p, .grf-p { font-size:12px; color:var(--text-muted); margin:2px 0 10px; line-height:1.45; }
          .grf-dim { font-size:11.5px; color:var(--text-muted); }
          .grf-hint { font-size:10.5px; color:var(--text-muted); margin-top:3px; line-height:1.35; }
          .grf-in { min-height:34px; padding:6px 10px; border:1px solid var(--border); border-radius:7px; font:inherit; font-size:13px; background:var(--card,#fff); color:var(--text); box-sizing:border-box; max-width:100%; }
          .grf-rates { display:grid; grid-template-columns:repeat(auto-fit, minmax(120px, 1fr)); gap:10px; margin-bottom:10px; }
          .grf-rates label, .grf-lbl { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); display:block; margin-bottom:4px; }
          .grf-bar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
          .grf-tiles { display:grid; grid-template-columns:repeat(auto-fit, minmax(140px, 1fr)); gap:10px; margin:12px 0; }
          .grf-tile { border:1px solid var(--border); border-radius:10px; padding:10px 12px; min-width:0; }
          .grf-tile b { display:block; font-size:18px; font-weight:600; font-variant-numeric:tabular-nums; color:var(--text); }
          .grf-tile span { font-size:11px; color:var(--text-muted); }
          .grf-tbl td, .grf-tbl th { white-space:nowrap; font-size:12px; padding:9px 10px; }
          .grf-ok { color:var(--success-text,#166534); } .grf-bad { color:var(--danger-text,#991b1b); font-weight:600; }
          .grf-tbl td.grf-prop { white-space:normal; min-width:170px; max-width:260px; }
          .grf-warn { font-size:11px; line-height:1.35; color:var(--warning-text,#92400e); margin-top:3px; }
          .grf-conf { margin:6px 0 0; padding-left:18px; font-size:12.5px; line-height:1.5; }
          .grf-form { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:14px; }
          .grf-checks { display:flex; flex-direction:column; gap:10px; margin-bottom:8px; }
          .grf-m-err { color:var(--danger-text,#991b1b); font-size:12.5px; margin-top:10px; }
          .grf-m-err:empty { display:none; }
          .grf-pick-list { max-height:min(50vh, 420px); overflow-y:auto; border:1px solid var(--border-soft); border-radius:8px; }
          .grf-pick { display:flex; gap:10px; align-items:center; min-height:40px; padding:6px 10px; border-bottom:1px solid var(--border-soft); font-size:13px; cursor:pointer; }
          .grf-pick .nm { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
          .grf-pick .pill { max-width:45%; overflow:hidden; text-overflow:ellipsis; display:inline-block; }
          @media (max-width: 640px) {
            .grf-form { grid-template-columns:1fr; }
            .grf-sub { padding:12px 14px 14px; }
            .grf-note, .grf-meta { padding:10px 14px; }
            #grf-modal-overlay { padding:16px 10px; }
            #grf-modal-overlay .cfg-modal-hdr { padding:14px 16px; }
            #grf-modal-overlay .cfg-modal-body { padding:16px; }
            #grf-modal-overlay .cfg-modal-footer { padding:12px 16px; }
          }
        </style>
        <div id="grf-root">${this.grossBodyHtml()}</div>
      </div>`;
  }

  private grossBodyHtml(): string {
    const s = this.gross;
    const hdr = `
      <div class="pf-section-hdr">
        <div>
          <h3>🧮 Statement formulas · Gross Revenue is built here</h3>
          <p>Each formula mirrors the VR Platform statement column by column: Gross Revenue = Rents + Resolutions/Fees + Cleaning Fees (when cleaning is In Gross), then Channel Fees, Net Revenue, Management Commission. Assign it to properties; every month without a published statement uses it on the owner portal, up to net before expenses.</p>
          <details class="grf-explain" id="grf-explain"><summary>Statement formula vs Commission (P&amp;L) formula</summary>
            <p><strong>Statement formula</strong> (here): ${grfEsc(EXPLAIN.statement.replace(/^Statement formula: /, ''))}</p>
            <p><strong>Commission (P&amp;L) formula</strong> (the Formula Library above): ${grfEsc(EXPLAIN.commission.replace(/^Commission \(P&L\) formula: /, ''))}</p>
            <p>${grfEsc(EXPLAIN.automatic)} They are separate on purpose; “Automatic” is the only place one borrows from the other.</p>
          </details>
        </div>
        <div class="grf-bar">
          <button class="btn btn-primary btn-sm" data-grf="new" ${s ? '' : 'disabled'}>+ New statement formula</button>
          <button class="btn btn-secondary btn-sm" data-grf="match" ${s && !(this.grossCalib && this.grossCalib.running) ? '' : 'disabled'}>Match the statements…</button>
        </div>
      </div>`;
    if (!s) return `${hdr}<div class="grf-note bad" role="alert">${grfEsc(this.grossErr || 'Loading…')} <button class="btn btn-secondary btn-sm" data-grf="reload">Reload</button></div>`;
    const note = this.grossNote ? `<div class="grf-note${this.grossNoteBad ? ' bad' : ''}" role="status">${grfEsc(this.grossNote)}</div>` : '';
    const saved = s.updated_at
      ? `Last saved ${new Date(s.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}${s.updated_by ? ` by ${s.updated_by}` : ''}`
      : 'Never saved — every property uses the built-in statement formula';
    return `${hdr}${note}
      <div class="u-table-wrap">
        <table class="data-table grf-tbl" id="grf-formulas" style="min-width:900px;">
          <thead><tr><th>Name</th><th>Statement shape</th><th class="num">Assigned</th><th></th></tr></thead>
          <tbody>${(s.rules || []).map((r: any) => this.grossRuleRow(r)).join('')}</tbody>
        </table>
      </div>
      <div class="grf-meta">${grfEsc(saved)} · ${(s.listings || []).length} active properties · one property at a time in <a href="#" data-grf="goto-ps">Portfolio → Property Settings</a></div>
      ${this.grossRatesHtml()}
      ${this.grossCalibHtml()}
      ${this.grossRecHtml()}`;
  }

  // R119: one row per statement formula — name + preset / legacy / default
  // badges, its shape in words (statementLadder.shapeChips), how many
  // properties resolve to it, and Edit · Preview · Assign… · Duplicate · Make
  // default · Delete. A pre-R119 rule's columns come from the legacy derivation.
  private grossRuleRow(r: any): string {
    const s = this.gross; const isDef = s.default_rule === r.id;
    const { all, explicit } = this.grossCount(r.id);
    const cols = formulaColumns(r);
    const presets = this.grossPresets();
    const preset = (PRESET_IDS as readonly string[]).includes(String(r.preset)) ? String(r.preset) : presetOf(cols, presets);
    const pl = presets.find(p => p.id === preset);
    // a pre-R119 rule's display adjustments to the gross the portal shows (schema 1 `display`, else its include / channel_fees)
    const inc = r.include || {}; const dsp = r.display && typeof r.display === 'object' ? r.display : { after: r.channel_fees === 'after', taxes: !!inc.taxes, company_fees: !!inc.company_fees };
    const disp = isLegacy(r) ? [dsp.after ? 'Gross shown after channel fees' : '', dsp.taxes ? 'Gross shown + taxes' : '', dsp.company_fees ? 'Gross shown + company fees' : ''].filter(Boolean) : [];
    const id = grfEsc(r.id);
    return `
      <tr data-grf-rule="${id}">
        <td class="grf-name"><strong title="${grfEsc(r.name)}">${grfEsc(r.name)}</strong>
          <div class="grf-badges"><span class="pill pill-gray" data-grf-preset="${grfEsc(preset)}" title="${grfEsc(pl ? pl.label : 'Custom — a shape no preset has')}">${grfEsc(presetName(preset))}</span>${isLegacy(r) ? '<span class="pill grf-legacy-pill" title="Made before R119 — cleaning follows each property\'s commission formula until it is saved in the builder">legacy</span>' : ''}${isDef ? '<span class="pill pill-blue">default</span>' : ''}</div>
          ${r.description ? `<div class="grf-dim">${grfEsc(r.description)}</div>` : ''}</td>
        <td><div class="grf-chips">${shapeChips(cols).map(c => `<span class="grf-chip">${grfEsc(c)}</span>`).join('')}${disp.map(c => `<span class="grf-chip grf-dim" title="Made before R119 — changes only the Gross the portal shows">${grfEsc(c)}</span>`).join('')}</div></td>
        <td class="num" data-grf-count="${id}">${all} propert${all === 1 ? 'y' : 'ies'}${isDef && all > explicit ? `<div class="grf-dim">${all - explicit} by default</div>` : ''}</td>
        <td><div class="grf-acts">
          <button class="btn btn-sm btn-secondary" data-grf="edit" data-id="${id}">Edit</button>
          <button class="btn btn-sm btn-ghost" data-grf="preview" data-id="${id}">Preview</button>
          <button class="btn btn-sm btn-secondary" data-grf="assign" data-id="${id}">Assign…</button>
          <button class="btn btn-sm btn-ghost" data-grf="dup" data-id="${id}">Duplicate</button>
          ${isDef ? '' : `<button class="btn btn-sm btn-outline" data-grf="default" data-id="${id}">Make default</button>`}
          ${r.id !== GRF_BUILTIN ? `<button class="btn btn-sm btn-danger" data-grf="del" data-id="${id}">Delete</button>` : ''}
        </div></td>
      </tr>`;
  }

  private grossRatesHtml(): string {
    const s = this.gross; const d = this.grossRatesDraft;
    const val = (k: string): string => (d && k in d ? d[k] : (s.channel_rates[k] == null ? '' : String(s.channel_rates[k])));
    return `
      <div class="grf-sub" id="grf-rates">
        <h4>Channel fee rates</h4>
        <p>% the channel charges on the owner's share of a booking (taxes excluded). Blank = use the fee on the booking itself. A property's own rates (from “Match the statements”) win over these.</p>
        <div class="grf-rates">${GRF_CH.map(([k, l]) => `<div><label for="grf-rate-${k}">${l}</label><input class="grf-in" id="grf-rate-${k}" data-grf-rate="${k}" type="number" min="0" max="50" step="0.1" inputmode="decimal" value="${grfEsc(val(k))}" placeholder="from the booking" style="width:100%;"></div>`).join('')}</div>
        <div class="grf-bar"><button class="btn btn-secondary btn-sm" data-grf="rates-save" ${d ? '' : 'disabled'}>Save rates</button><span class="grf-dim" id="grf-rates-state">${d ? 'Unsaved changes.' : ''}</span></div>
      </div>`;
  }

  // the cells a per-property statement profile shows (learned or saved)
  private grossProfileCells(p: any): string {
    const v = p || {};
    const yn = (b: unknown) => (b === true ? 'yes' : b === false ? 'no' : '—');
    const com = grfNum(v.commission_rate) == null ? '—' : `${grfPct(v.commission_rate)}${v.commission_base ? ` of ${v.commission_base}` : ''}`;
    const cr = v.channel_rates && typeof v.channel_rates === 'object' ? v.channel_rates : {};
    const ch = GRF_PCH.filter(([k]) => grfNum(cr[k]) != null).map(([k, l]) => `${l} ${grfPct(cr[k])}`).join(' · ');
    // R118 review #2: the Airbnb tax % and the direct totals' own (+ Booking.com's, when one is set)
    const bkg = grfNum(v.tot_pct_bookingcom) == null ? '' : `<div class="grf-dim">Booking.com ${grfPct(v.tot_pct_bookingcom)}</div>`;
    // R121: − the cleaning cost per turnover; + the company fee by booking date (its schedule in words)
    return `<td class="num">${grfMoney(v.company_fee)}</td><td class="num">${grfPct(v.tot_pct)}</td><td class="num" data-grf-tax-direct>${grfPct(v.tot_pct_direct)}${bkg}</td><td>${yn(v.cleaning_in_gross)}${v.cf_on_cleaning === true ? '<div class="grf-dim">ch. fee on cleaning</div>' : ''}</td><td>${com}</td><td>${grfEsc(ch || '—')}${v.vrbo_fee_basis === 'guest_total' ? '<div class="grf-dim">Vrbo on guest total</div>' : ''}</td><td data-grf-fee-sched>${grfEsc(scheduleText(v.company_fee_schedule) || '—')}</td>`;
  }

  // R118 review #11: the SERVER's verdict, never a copy of its rule. The old
  // copy compared after vs before only — a re-run is judged on the in-sample
  // fit_pct, and 'nothing learned' is skipped — so a row could read "no better
  // — skipped" (and Apply sit at 0, disabled) where the server applies. A dry-
  // run row carries applyCalibration's own answer (will_apply / why, and the
  // same with "also replace the ones set by hand"); after Apply the response's
  // `skipped` says what it did — everything not listed was applied.
  // → apply: true / false, or null = not known (re-check that row)
  private grossVerdict(c: any, l: any): { apply: boolean | null; why: string } {
    if (c && c.dry_run) {
      const ow = this.grossOverwriteManual;
      // R119 review fix-verify: with "Also assign" ticked the server writes facts only and
      // gates them on a different score — its answer for that case is will_apply_assign(_overwrite)
      const asg = this.grossCalibAssign && typeof l.will_apply_assign === 'boolean';
      const will = asg ? (ow ? l.will_apply_assign_overwrite : l.will_apply_assign) : (ow ? l.will_apply_overwrite : l.will_apply);
      if (typeof will !== 'boolean') return { apply: null, why: 'changed — run the check again to see' };
      const why = asg ? (ow ? l.why_apply_assign_overwrite : l.why_apply_assign) : (ow ? l.why_overwrite : l.why);
      return { apply: will, why: will ? '' : String(why || 'skipped') };
    }
    const sk = (c && Array.isArray(c.skipped) ? c.skipped : []).find((s: any) => String(s.listing_id) === String(l.listing_id));
    return sk ? { apply: false, why: String(sk.why || 'skipped') } : { apply: true, why: '' };
  }
  // R119 review #14: what "Also assign the learned statement formula" does for a row — the
  // SERVER's answer (previewCalibration will_assign / why_assign; after Apply the response's
  // assign_skipped), never a copy of its rule. R119 fix-verify: "Also replace the ones set by
  // hand" never lifts the guard on a statement formula chosen by hand (review #5 — lib/
  // ownerCalibrate target()); it replaces FACTS set by hand, which can let the assign through
  // (review #6) — the dry run answers that too (will_assign_overwrite). null = the row's
  // verdict is gone (its re-check failed: grossRecheckRow) → "maybe" (said, ticked, counted as
  // "up to … whose check needs re-running").
  // → assign: true / false / null, or undefined = no assign asked (after an Apply without it)
  private grossAssignVerdict(c: any, l: any): { assign: boolean | null | undefined; why: string } {
    if (!c || !c.dry_run) {
      if (!c || !Array.isArray(c.assign_skipped)) return { assign: undefined, why: '' };
      const sk = c.assign_skipped.find((s: any) => String(s.listing_id) === String(l.listing_id));
      return sk ? { assign: false, why: String(sk.why || 'not assigned') } : { assign: true, why: '' };
    }
    if (!l || !l.suggested_formula) return { assign: false, why: 'no statement formula learned' };
    const ow = this.grossOverwriteManual;
    if (ow && typeof l.will_assign_overwrite === 'boolean') return { assign: l.will_assign_overwrite, why: l.will_assign_overwrite ? '' : String(l.why_assign_overwrite || 'not assigned') };
    if (typeof l.will_assign !== 'boolean') return { assign: null, why: 'changed — run the check again to see' };
    if (l.will_assign) return { assign: true, why: '' };
    const why = String(l.why_assign || 'not assigned');
    // (R119 review #5: calibrate never replaces a formula chosen by hand, "Also replace" or not —
    //  the builder's Learn → Assign is the way; so there is no "maybe" for it)
    return { assign: false, why };
  }
  // R119 review #14: a dry-run row carries a tick when Apply can do something to it — its
  // learned facts (the server's will_apply) or, with "Also assign" ticked, its suggested
  // statement formula (will_assign / will_assign_overwrite, or "maybe" when its re-check failed —
  // R119 fix-verify). The ticks ARE the scope: with "Also assign" Apply always sends
  // listing_ids = the ticked rows (the server requires it).
  private grossTickable(c: any, l: any): boolean {
    if (!c || !c.dry_run || !l) return false;
    if (this.grossVerdict(c, l).apply === true) return true;
    return this.grossCalibAssign && !!l.suggested_formula && this.grossAssignVerdict(c, l).assign !== false;
  }
  private grossApplyCount(c: any): number {
    return this.grossApplyRows(c).length;
  }
  // R119: the rows Apply sends — a dry run's tickable rows the office left ticked (after an
  // Apply: the rows it applied)
  private grossApplyRows(c: any): any[] {
    const list: any[] = (c && Array.isArray(c.listings) && c.listings) || [];
    if (!c || !c.dry_run) return list.filter((l: any) => this.grossVerdict(c, l).apply === true);
    return list.filter((l: any) => this.grossTickable(c, l) && !this.grossCalibUntick.has(String(l.listing_id)));
  }
  // R119 review #14: what Apply does to the ticked rows, counted apart — facts (profiles) and
  // statement formulas (assigns; created = the preset formulas sf_preset_<X> Apply makes
  // because they don't exist yet). R119 fix-verify: maybe / factsMaybe = ticked rows whose
  // re-check failed (null verdicts) — sent with "Also assign", so the server may write their
  // formula AND their facts: both counts say "up to"
  private grossApplyPlan(c: any): { rows: any[]; facts: number; factsMaybe: number; assign: number; maybe: number; created: string[] } {
    const rows = this.grossApplyRows(c);
    const out = { rows, facts: 0, factsMaybe: 0, assign: 0, maybe: 0, created: [] as string[] };
    const have = new Set((((this.gross && this.gross.rules) || []) as any[]).map((r: any) => String(r.id)));
    for (const l of rows) {
      const fv = this.grossVerdict(c, l).apply;
      if (fv === true) out.facts++; else if (fv === null) out.factsMaybe++;
      if (!this.grossCalibAssign) continue;
      const v = this.grossAssignVerdict(c, l).assign;
      if (v === true) out.assign++; else if (v === null) out.maybe++; else continue;
      const sf = l.suggested_formula || {};
      const id = sf.id && have.has(String(sf.id)) ? '' : sf.preset && sf.preset !== 'auto' && sf.preset !== 'custom' ? `sf_preset_${sf.preset}` : '';
      if (id && !have.has(id) && !out.created.includes(String(sf.name || id))) out.created.push(String(sf.name || id));
    }
    return out;
  }
  private grossAssignHtml(c: any, l: any): string {
    const v = this.grossAssignVerdict(c, l);
    if (v.assign === undefined) return '';
    // R119 fix-verify: "chosen by hand" only for a FORMULA chosen by hand (target()'s why) — an
    // assign held back because the property's FACTS were set by hand (review #6: "…aren't written
    // (set by hand)") says so; "Also replace the ones set by hand" can lift that one, never the other
    const short = (w: string) => (/chosen by hand/i.test(w) ? 'kept — chosen by hand' : /\(set by hand\)/i.test(w) ? 'its facts were set by hand' : /fewer statements/i.test(w) ? 'kept — the learned one matches fewer statements' : /no preset/i.test(w) ? 'no preset has this shape — save it from the builder' : /no room/i.test(w) ? 'no room for another formula' : w);
    if (!c.dry_run) return v.assign ? '<div class="grf-ok" data-grf-asg="1">uses it now</div>' : `<div class="grf-dim" data-grf-asg="0" title="${grfEsc(v.why)}">not assigned — ${grfEsc(short(v.why))}</div>`;
    const on = this.grossCalibAssign;
    if (v.assign === true) return `<div class="${on ? 'grf-ok' : 'grf-dim'}" data-grf-asg="1">${on ? 'will assign' : '“Also assign” points it here'}</div>`;
    if (v.assign === null) return `<div class="grf-dim" data-grf-asg="?" title="${grfEsc(v.why)}">${on ? 'may assign' : '“Also assign” may point it here'} — ${grfEsc(v.why)}</div>`;   // null = its re-check failed (grossAssignVerdict)
    return `<div class="grf-dim" data-grf-asg="0" title="${grfEsc(v.why)}">${on ? 'won\'t assign' : 'not assigned'} — ${grfEsc(short(v.why))}</div>`;
  }
  private grossVerdictHtml(c: any, l: any): string {
    const v = this.grossVerdict(c, l);
    if (v.apply === true) return c.dry_run ? '<span class="grf-ok">will apply</span>' : '<span class="grf-ok">applied</span>';
    const w = v.why;
    const short = v.apply === null ? w : /by hand/i.test(w) ? 'kept — set by hand' : /fewer than 2/i.test(w) ? 'too few samples' : /match(es)? fewer/i.test(w) ? 'no better — skipped' : /nothing learned/i.test(w) ? 'nothing learned — skipped' : `skipped — ${w}`;
    return `<span class="grf-dim" title="${grfEsc(w)}">${grfEsc(short)}</span>`;
  }

  private grossCalibHtml(): string {
    const s = this.gross; const c = this.grossCalib; const last = s.calibration;
    const lastLine = last && last.at
      ? `Last applied ${new Date(last.at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}${last.by ? ` by ${last.by}` : ''}${Array.isArray(last.months) && last.months.length ? ` · statements ${last.months[0]} – ${last.months[last.months.length - 1]}` : ''} · rows within $1: ${grfPct(last.before_pct)} → ${grfPct(last.after_pct)}`
      : 'Learned settings have not been applied yet.';
    const opt = (v: string, l: string) => `<option value="${v}" ${this.grossCalibMonths === v ? 'selected' : ''}>${l}</option>`;
    return `
      <div class="grf-sub" id="grf-calib">
        <h4>Match the statements</h4>
        <p>Reads the published owner statements and learns each property's facts: the company fee per booking (damage waiver, the Mission Beach fee…) and when it changed (the fee in force when each booking was made), the occupancy tax % inside Airbnb and direct-booking totals, the commission rate and the Vrbo fee's base. It also learns the statement formula its statements follow (where cleaning goes, what the channel fee and the commission are taken on). Then it shows how many statement rows the portal matches now and with what it learned. Nothing is saved until you apply.</p>
        <div class="grf-dim" style="margin-bottom:8px;">${grfEsc(lastLine)}</div>
        <div class="grf-bar">
          <select class="grf-in" id="grf-cal-months" aria-label="Statement months">${opt('', 'Every published month')}${opt('3', 'Last 3 months')}${opt('6', 'Last 6 months')}${opt('12', 'Last 12 months')}</select>
          <button class="btn btn-primary btn-sm" data-grf="calib-run" ${c && c.running ? 'disabled' : ''}>${c && c.running && c.dry_run ? 'Checking…' : 'Check against statements'}</button>
          ${c && !c.running ? '<button class="btn btn-ghost btn-sm" data-grf="calib-close">Close results</button>' : ''}
        </div>
        ${c ? this.grossCalibResultHtml(c) : this.grossProfilesHtml()}
      </div>`;
  }

  private grossCalibResultHtml(c: any): string {
    if (c.running) return `<div class="grf-dim" style="margin-top:10px;">${c.dry_run ? 'Checking the statements…' : 'Applying the learned settings…'}</div>`;
    if (c.error) return `<div class="grf-bad" style="margin-top:10px; font-size:12.5px;" role="alert">${grfEsc(c.error)}</div>`;
    const o = c.overall || {}; const rows = c.rows || {};
    const list: any[] = Array.isArray(c.listings) ? c.listings : [];
    const conf: any[] = Array.isArray(c.conflicts) ? c.conflicts : [];
    const nApply = this.grossApplyCount(c);
    // the rows "also replace the ones set by hand" would ADD (the server's answer with overwrite)
    // R119 fix-verify: with "Also assign" ticked the server answers per flag in assign mode
    const nManual = list.filter(l => (this.grossCalibAssign && typeof l.will_apply_assign === 'boolean')
      ? (l.will_apply_assign === false && l.will_apply_assign_overwrite === true) || (l.will_assign === false && l.will_assign_overwrite === true)
      : l.will_apply === false && l.will_apply_overwrite === true).length;
    const KIND: Record<string, string> = { commission_base: 'Commission base', commission_rate: 'Commission rate', cleaning: 'Cleaning', mapping: 'Statement ↔ property match', tax: 'Occupancy tax' };
    const tile = (v: string, l: string) => `<div class="grf-tile"><b>${v}</b><span>${l}</span></div>`;
    const months = Array.isArray(c.months) && c.months.length ? `${c.months[0]} – ${c.months[c.months.length - 1]}` : '';
    return `
      <div class="grf-tiles" id="grf-cal-overall">
        ${tile(`${Number(rows.matched) || 0} / ${Number(rows.total) || 0}`, `statement rows matched to bookings${months ? ` · ${grfEsc(months)}` : ''}`)}
        ${tile(grfPct(o.before_pct), 'rows within $1 of the statement now')}
        ${tile(grfPct(o.after_pct), 'rows within $1 with the learned settings')}
        ${tile(`${grfPct(o.gap_before_pct)} → ${grfPct(o.gap_after_pct)}`, 'portfolio gross gap')}
      </div>
      ${list.length ? `<div class="u-table-wrap">
        <table class="data-table grf-tbl" id="grf-cal-tbl" style="min-width:1060px;">
          <thead><tr><th>Property · warnings</th><th class="num">Samples</th><th class="num">Match now</th><th class="num">With learned</th><th class="num">Company fee</th><th class="num">Airbnb tax %</th><th class="num">Direct tax %</th><th>Cleaning in gross</th><th>Commission</th><th>Channel %</th><th>Company fee by date</th><th>Suggested formula</th><th>Status</th><th></th>${c.dry_run ? '<th title="Untick a property to leave it out of Apply">Apply</th>' : ''}</tr></thead>
          <tbody>${list.map(l => {
            const better = (Number(l.after_pct) || 0) > (Number(l.before_pct) || 0);
            const lid = grfEsc(l.listing_id);
            const will = this.grossTickable(c, l);   // R119 review #14: facts to apply, or (Also assign) a formula
            return `<tr data-grf-cal="${lid}">
              <td class="grf-prop">${grfEsc(l.name || l.listing_id)}${(Array.isArray(l.warnings) ? l.warnings : []).map((w: unknown) => `<div class="grf-warn">⚠ ${grfEsc(w)}</div>`).join('')}</td>
              <td class="num">${Number(l.samples) || 0}</td>
              <td class="num">${grfPct(l.before_pct)}</td>
              <td class="num${better ? ' grf-ok' : ''}">${grfPct(l.after_pct)}</td>
              ${this.grossProfileCells(l.learned)}
              <td class="grf-sf" data-grf-sf="${lid}">${this.grossSuggestedHtml(l)}${l && l.suggested_formula ? this.grossAssignHtml(c, l) : ''}</td>
              <td data-grf-state="${lid}">${this.grossVerdictHtml(c, l)}</td>
              <td><button class="btn btn-sm btn-secondary" data-grf="profile" data-lid="${lid}">Edit</button></td>
              ${c.dry_run ? `<td style="text-align:center;">${will ? `<input type="checkbox" class="grf-tick" data-grf-cal-tick="${lid}" aria-label="Apply to ${grfEsc(l.name || l.listing_id)}" ${this.grossCalibUntick.has(String(l.listing_id)) ? '' : 'checked'}>` : '<span class="grf-dim">—</span>'}</td>` : ''}
            </tr>`;
          }).join('')}</tbody>
        </table>
      </div>` : '<div class="grf-dim">No property had statement rows to learn from.</div>'}
      ${conf.length ? `<div class="grf-lbl" style="margin-top:14px;">To look at (${conf.length})</div><ul class="grf-conf" id="grf-cal-conflicts">${conf.map(x => `<li><strong>${grfEsc(x.name || x.listing_id)}</strong> · ${grfEsc(KIND[x.kind] || x.kind)} — ${grfEsc(x.detail)}</li>`).join('')}</ul>` : ''}
      ${c.dry_run ? `
        <div class="grf-bar" style="margin-top:12px;">
          ${nManual || this.grossOverwriteManual ? `<label class="cfg-checkbox-row"><input type="checkbox" id="grf-cal-overwrite" ${this.grossOverwriteManual ? 'checked' : ''}> Also replace the ${nManual} set by hand</label>` : ''}
          ${list.some(l => l && l.suggested_formula) ? `<label class="cfg-checkbox-row" title="${grfEsc(GRF_ASSIGN_TIP)}"><input type="checkbox" id="grf-cal-assign" ${this.grossCalibAssign ? 'checked' : ''}> Also assign the learned statement formula</label>` : ''}
          <button class="btn btn-primary btn-sm" data-grf="calib-apply" ${nApply ? '' : 'disabled'}>Apply learned settings (${nApply})</button>
          <span class="grf-dim" id="grf-cal-plan">${grfEsc(this.grossPlanWords(c))}</span>
        </div>` : this.grossAppliedHtml(c)}`;
  }

  // R119 review #14: the dry run's footnote — what Apply does, facts and formulas apart
  private grossPlanWords(c: any): string {
    const base = 'Facts apply only where there are 2+ samples and the match is at least as good.';
    if (!this.grossCalibAssign) return base;
    const p = this.grossApplyPlan(c);
    return `${base} Statement formula: ${p.assign} to point at the suggested one${p.maybe ? ` (+ up to ${p.maybe} still to re-check)` : ''}${p.created.length ? `, creating ${p.created.join(', ')}` : ''}.`;
  }
  // R119 review #14: after Apply — the response's applied, and with "Also assign" its assigned,
  // created and assign_skipped (each with why), not only "Applied … to N"
  private grossAppliedHtml(c: any): string {
    const a = Number(c.applied) || 0;
    let html = `<div class="grf-ok" style="margin-top:12px; font-weight:600;" id="grf-cal-applied">✓ Applied to ${grfProps(a)}${Array.isArray(c.assign_skipped) ? ' — their learned facts' : ''}.</div>`;
    if (!Array.isArray(c.assign_skipped)) return html;
    const list: any[] = Array.isArray(c.listings) ? c.listings : [];
    const skipped: any[] = c.assign_skipped;
    const pointed = list.filter(l => !skipped.some((s: any) => String(s.listing_id) === String(l.listing_id))).length;
    const moved = Number(c.assigned) || 0;
    const created: string[] = (Array.isArray(c.created) ? c.created : []).map((id: unknown) => this.grossRuleName(String(id)));
    const name = (id: unknown) => { const l = list.find(x => String(x.listing_id) === String(id)); return String((l && l.name) || this.grossListingName(String(id))); };
    html += `<div class="grf-p" style="margin-top:6px;" id="grf-cal-assigned"><strong>Statement formula:</strong> ${grfProps(pointed)} now use${pointed === 1 ? 's' : ''} the suggested one (${moved} re-pointed${pointed - moved > 0 ? `, ${pointed - moved} already did` : ''})${created.length ? `; created ${created.map(n => `“${grfEsc(n)}”`).join(', ')}` : ''}.</div>`;
    if (skipped.length) html += `<div class="grf-p" style="margin-top:4px;" id="grf-cal-assign-skipped"><strong>Not assigned (${skipped.length}):</strong> ${skipped.map((s: any) => `${grfEsc(name(s.listing_id))} — ${grfEsc(s.why || 'not assigned')}`).join('; ')}.</div>`;
    return html;
  }

  // the saved per-property profiles (shown when no check result is on screen)
  private grossProfilesHtml(): string {
    const s = this.gross; const profiles = s.profiles || {};
    const ids = Object.keys(profiles).filter(id => profiles[id]);
    if (!ids.length) return `<div class="grf-dim" style="margin-top:10px;">No property facts saved yet — “Check against statements” learns them, or open a formula's Preview → Edit property facts.</div>`;
    ids.sort((a, b) => this.grossListingName(a).localeCompare(this.grossListingName(b)));
    return `
      <div class="grf-lbl" style="margin-top:14px;">Property facts (${ids.length})</div>
      <div class="u-table-wrap">
        <table class="data-table grf-tbl" style="min-width:1020px;">
          <thead><tr><th>Property</th><th>Set by</th><th class="num">Samples</th><th class="num">Match</th><th class="num">Company fee</th><th class="num">Airbnb tax %</th><th class="num">Direct tax %</th><th>Cleaning in gross</th><th>Commission</th><th>Channel %</th><th>Company fee by date</th><th></th></tr></thead>
          <tbody>${ids.map(id => { const p = profiles[id]; return `<tr>
            <td class="u-truncate" style="max-width:220px;" title="${grfEsc(this.grossListingName(id))}">${grfEsc(this.grossListingName(id))}</td>
            <td>${p.source === 'manual' ? 'by hand' : 'statements'}</td>
            <td class="num">${Number(p.samples) || 0}</td>
            <td class="num">${grfPct(p.match_pct)}</td>
            ${this.grossProfileCells(p)}
            <td><button class="btn btn-sm btn-secondary" data-grf="profile" data-lid="${grfEsc(id)}">Edit</button></td>
          </tr>`; }).join('')}</tbody>
        </table>
      </div>`;
  }

  private grossRecHtml(): string {
    const rec = this.grossRec;
    const money = (v: unknown) => `$${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    let out = '';
    if (rec && rec.loading) out = '<div class="grf-dim">Comparing…</div>';
    else if (rec && rec.error) out = `<div class="grf-bad" style="font-size:12.5px;" role="alert">${grfEsc(rec.error)}</div>`;
    else if (rec && !(rec.rows || []).length) out = `<div class="grf-dim">${grfEsc(rec.basis || 'No published statements for this month yet.')}</div>`;
    else if (rec) out = `
      <div class="u-table-wrap">
        <table class="data-table grf-tbl" id="grf-rec-tbl" style="min-width:1080px;">
          <thead><tr><th>Property</th><th>Statement formula · commission formula</th><th class="num">Gross (formula / statement)</th><th class="num">Gap</th><th class="num">Channel fee gap</th><th class="num">Non-comm gap</th><th class="num">Commission gap</th><th class="num">Net before expenses (formula / statement)</th><th class="num">Gap</th><th class="num">Expenses (logged in Dash / statement)</th></tr></thead>
          <tbody>${rec.rows.map((r: any) => {
            // R119: the ladder (formula_ladder / statement_ladder / gap per ladder key) when the server sends it; an R118 row keeps its gross / fee gaps
            const fl = r.formula_ladder || {}; const sl = r.statement_ladder || {}; const gap = r.gap || {};
            const g = Number(gap.gross) || 0;
            // R121: net before expenses (total − commission) — a formula has no net income (expenses come from the statement)
            const nf = grfNum(fl.net_before_expenses != null ? fl.net_before_expenses : fl.owner_net), ns = grfNum(sl.owner_net);
            const ng = grfNum(gap.owner_net) != null ? Number(gap.owner_net) : nf != null && ns != null ? Math.round((nf - ns) * 100) / 100 : null;
            const ex = r.expenses || {};
            const el = grfNum(ex.logged != null ? ex.logged : ex.formula), es = grfNum(ex.statement);
            const gc = (v: unknown) => (grfNum(v) == null ? '—' : grfGap(v));
            return `<tr data-grf-rec="${grfEsc(r.listing_id)}">
            <td class="u-truncate" style="max-width:220px;" title="${grfEsc(r.property)}">${grfEsc(r.property)}</td>
            <td style="color:var(--text-muted);">${grfEsc(this.grossRuleName(r.rule_id))} · ${grfEsc(r.formula)} · commission ${r.commission_on === 'after_channel_fees' ? 'after' : 'with'} channel fees</td>
            <td class="num">${money(fl.gross != null ? fl.gross : (r.formula_money || {}).gross)} / ${money(sl.gross != null ? sl.gross : (r.statement_money || {}).gross)}</td>
            <td class="num ${Math.abs(g) < 1 ? 'grf-ok' : 'grf-bad'}">${grfGap(g)}</td>
            <td class="num">${gc(gap.channel_fee)}</td>
            <td class="num">${gc(gap.noncomm)}</td>
            <td class="num">${gc(gap.commission != null ? gap.commission : gap.mgmt_fee)}</td>
            <td class="num">${nf == null && ns == null ? '—' : `${nf == null ? '—' : fmtMoney(nf)} / ${ns == null ? '—' : fmtMoney(ns)}`}</td>
            <td class="num ${ng == null ? '' : Math.abs(ng) < 1 ? 'grf-ok' : 'grf-bad'}">${ng == null ? '—' : grfGap(ng)}</td>
            <td class="num">${el == null && es == null ? '—' : `${el == null ? '—' : fmtMoney(el)} / ${es == null ? '—' : fmtMoney(es)}`}</td>
          </tr>`; }).join('')}</tbody>
        </table>
      </div>
      <div class="grf-dim" style="margin-top:6px;">${grfEsc(rec.basis || '')}. Owners see the statement's own numbers for any month whose statement is published — the formulas decide the months before it is.</div>`;
    return `
      <div class="grf-sub" id="grf-rec">
        <h4>Compare a month</h4>
        <p>One month with published statements, property by property, down the statement ladder to net before expenses: the gap shows which formula (or property fact) needs adjusting. Expenses come from the statement, next to what Dash logged. A formula's Preview shows the same comparison booking by booking, with why each cell is off.</p>
        <div class="grf-bar" style="margin-bottom:10px;"><input class="grf-in" type="month" id="grf-rec-month" value="${grfEsc(this.grossRecMonth)}" aria-label="Month"><button class="btn btn-secondary btn-sm" data-grf="rec-go">Compare</button></div>
        ${out}
      </div>`;
  }

  // ── gross: events (delegated — the section's inner HTML redraws freely) ──
  private bindGross(): void {
    const sec = this.container.querySelector<HTMLElement>('#grf-section');
    const ov = this.container.querySelector<HTMLElement>('#grf-modal-overlay');
    if (!sec || !ov) return;
    const onClick = (e: Event) => {
      const t = e.target as HTMLElement;
      if (t === ov) { this.closeGrossModal(); return; }
      const b = t.closest<HTMLElement>('[data-grf]');
      if (!b || (b as HTMLButtonElement).disabled) return;
      if (b.tagName === 'A') e.preventDefault();
      void this.grossAction(b.dataset.grf || '', b);
    };
    sec.addEventListener('click', onClick);
    ov.addEventListener('click', onClick);
    ov.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Escape') this.closeGrossModal(); });
    sec.addEventListener('input', (e) => {
      const el = e.target as HTMLInputElement;
      const k = el.dataset ? el.dataset.grfRate : undefined;
      if (!k) return;
      this.grossRatesDraft = { ...(this.grossRatesDraft || {}), [k]: el.value };
      const btn = sec.querySelector<HTMLButtonElement>('[data-grf="rates-save"]'); if (btn) btn.disabled = false;
      const st = sec.querySelector<HTMLElement>('#grf-rates-state'); if (st) st.textContent = 'Unsaved changes.';
    });
    sec.addEventListener('change', (e) => {
      const el = e.target as HTMLInputElement;
      if (el.id === 'grf-cal-months') this.grossCalibMonths = el.value;
      else if (el.id === 'grf-rec-month') this.grossRecMonth = el.value;
      else if (el.id === 'grf-cal-overwrite') { this.grossOverwriteManual = el.checked; this.drawGross(); }
      // R119 review #14: "Also assign" changes which rows carry a tick and what each one says
      else if (el.id === 'grf-cal-assign') { this.grossCalibAssign = el.checked; this.drawGross(); }
      else if (el.dataset && el.dataset.grfCalTick != null) {   // R119: a per-row tick (Apply leaves an unticked row out)
        if (el.checked) this.grossCalibUntick.delete(el.dataset.grfCalTick); else this.grossCalibUntick.add(el.dataset.grfCalTick);
        const n = this.grossApplyCount(this.grossCalib);
        const btn = sec.querySelector<HTMLButtonElement>('[data-grf="calib-apply"]');
        if (btn) { btn.textContent = `Apply learned settings (${n})`; btn.disabled = !n; }
        const plan = sec.querySelector<HTMLElement>('#grf-cal-plan'); if (plan) plan.textContent = this.grossPlanWords(this.grossCalib);
      }
    });
    ov.addEventListener('input', (e) => {
      const el = e.target as HTMLInputElement;
      if (el.id === 'grf-pick-search') { this.grossPickSearch = el.value; this.drawGrossPickList(); }
    });
    ov.addEventListener('change', (e) => {
      const el = e.target as HTMLInputElement;
      const id = el.dataset ? el.dataset.grfPick : undefined;
      if (id == null) return;
      if (el.checked) this.grossPick.add(id); else this.grossPick.delete(id);
      this.drawGrossPickCount();
    });
  }

  private async grossAction(a: string, el: HTMLElement): Promise<void> {
    const id = el.dataset.id || '';
    switch (a) {
      case 'reload': await this.loadGross(); this.drawGross(); return;
      case 'goto-ps':
        try { sessionStorage.setItem('hub_tab', 'settings'); } catch { /* private mode */ }
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'portfolio' }));
        return;
      // R119: New / Edit / Preview / Duplicate open the statement formula builder
      case 'new': return this.openBuilder({ formula: null });
      case 'edit': return this.openBuilder({ formula: this.grossRule(id) });
      case 'preview': return this.openBuilder({ formula: this.grossRule(id), mode: 'preview' });
      case 'dup': return this.openBuilder({ formula: this.grossRule(id), copy: true });
      case 'match':
        await this.grossCalibrate(true);
        this.container.querySelector<HTMLElement>('#grf-calib')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      case 'assign': return this.openGrossAssignModal(this.grossRule(id));
      case 'del': return this.grossDelete(id);
      case 'default': return this.grossMakeDefault(id);
      case 'rates-save': return this.grossSaveRates();
      case 'calib-run': return this.grossCalibrate(true);
      case 'calib-apply': return this.grossCalibrate(false);
      case 'calib-close': this.grossCalib = null; this.drawGross(); return;
      case 'profile': return this.openGrossProfileModal(el.dataset.lid || '');
      case 'rec-go': return this.grossReconcile();
      case 'm-close': case 'm-cancel': this.closeGrossModal(); return;
      case 'pick-all': for (const l of this.grossPickShown()) this.grossPick.add(String(l.id)); this.drawGrossPickList(); return;
      case 'pick-none': for (const l of this.grossPickShown()) this.grossPick.delete(String(l.id)); this.drawGrossPickList(); return;
      case 'assign-save': return this.grossSaveAssign();
      case 'profile-save': return this.grossSaveProfile(false);
      case 'profile-clear': return this.grossSaveProfile(true);
    }
  }

  // R119: the Suggested formula a Match learned for one property (calibrate
  // listing.suggested_formula { id|null, preset, name }) — null id = Apply with
  // "Also assign" creates it (the preset formulas sf_preset_<X>)
  private grossSuggestedHtml(l: any): string {
    const sf = l && l.suggested_formula;
    if (!sf || typeof sf !== 'object') return '<span class="grf-dim">—</span>';
    const presets = this.grossPresets();
    const p = presets.find(x => x.id === sf.preset);
    const name = sf.id ? this.grossRuleName(String(sf.id)) : String(sf.name || (p ? p.label : 'New formula'));
    const cur = this.gross && this.gross.assign ? (this.gross.assign[String(l.listing_id)] || this.gross.default_rule) : '';
    const same = sf.id && String(sf.id) === String(cur);
    return `<b title="${grfEsc(p ? p.label : '')}">${grfEsc(name)}</b> <span class="pill pill-gray">${grfEsc(presetName(String(sf.preset || 'custom')))}</span>${sf.id ? '' : '<div class="grf-dim">new — created on Apply</div>'}${same ? '<div class="grf-dim">already uses it</div>' : ''}`;
  }

  // ── gross: the one modal (assign / per-property facts) ────────────────────
  private openGrossModal(kind: 'assign' | 'profile', id: string, title: string, body: string, footer: string): void {
    const ov = this.container.querySelector<HTMLElement>('#grf-modal-overlay');
    const t = this.container.querySelector<HTMLElement>('#grf-modal-title');
    const b = this.container.querySelector<HTMLElement>('#grf-modal-body');
    const f = this.container.querySelector<HTMLElement>('#grf-modal-footer');
    if (!ov || !t || !b || !f) return;
    this.grossModal = { kind, id };
    t.textContent = title; b.innerHTML = body; f.innerHTML = footer;
    ov.classList.add('open');
  }
  private closeGrossModal(): void {
    this.container.querySelector('#grf-modal-overlay')?.classList.remove('open');
    this.grossModal = null;
  }
  private grossModalErr(msg: string): void { const el = this.container.querySelector('#grf-m-err'); if (el) el.textContent = msg; }
  private grossModalBtn(a: string): HTMLButtonElement | null { return this.container.querySelector<HTMLButtonElement>(`#grf-modal-footer [data-grf="${a}"]`); }

  // R118 review #14: a patch (/assign, /profile, /calibrate apply) moves the
  // store's version, and the only way this page learned the new one was the
  // follow-up GET — a failed GET (or a partly failed assign, which skipped it)
  // left the old version, so our OWN next save 409'd as "someone else". Each
  // patch now returns the whole store it stamped; adopt the version only WITH
  // that snapshot — a bare version would let our next PUT, drawn from an older
  // read, pass the 409 check and put back what someone else changed in between.
  // A response older than what we hold is ignored. → adopted?
  private adoptGrossStore(j: any): boolean {
    const st = j && j.store; const s = this.gross;
    if (!s || !st || typeof st !== 'object' || !Array.isArray(st.rules) || !j.version) return false;
    const held = Date.parse(String(s.version || s.updated_at || '')); const got = Date.parse(String(j.version));
    if (Number.isFinite(held) && Number.isFinite(got) && got < held) return false;
    this.gross = { ...s, ...st, assign: st.assign || {}, profiles: st.profiles || {}, channel_rates: st.channel_rates || {}, version: j.version, updated_at: st.updated_at || j.version };
    return true;
  }

  // PUT the store with its version — rules / default / channel rates only
  // (see the block comment above: never assign / profiles). 409 → reloaded.
  private async grossPut(patch: Record<string, unknown>): Promise<{ ok: boolean; stale?: boolean; error?: string }> {
    const s = this.gross;
    const body = { rules: s.rules, default_rule: s.default_rule, channel_rates: s.channel_rates, ...patch, version: s.version ?? s.updated_at ?? '' };
    try {
      const r = await fetch('/api/owner-finance/rules', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { await this.loadGross(); return { ok: false, stale: true, error: j.error || 'Someone else saved the statement formulas a moment ago.' }; }
      if (!r.ok) return { ok: false, error: j.error || `Not saved (HTTP ${r.status}).` };
      if (j && Array.isArray(j.rules)) this.gross = { ...this.gross, ...j, version: j.version ?? j.updated_at ?? s.version };
      await this.loadGross();   // fresh version + listings (the merge above stands if this read fails)
      return { ok: true };
    } catch (e: any) { return { ok: false, error: `Not saved — ${(e && e.message) || 'network error'}.` }; }
  }

  // R119: DELETE /api/owner-finance/formulas/:id — one formula, no whole-store
  // version; the server drops its assignments (they go back to the default)
  // and resets the default to the built-in when it was the default.
  private async grossDelete(id: string): Promise<void> {
    const r = this.grossRule(id);
    if (!r || id === GRF_BUILTIN || this.grossBusy) return;
    const { explicit } = this.grossCount(id); const isDef = this.gross.default_rule === id;
    const what = `${explicit ? ` Its ${explicit} propert${explicit === 1 ? 'y goes' : 'ies go'} back to the default formula.` : ''}${isDef ? ' The built-in statement formula becomes the default.' : ''}`;
    if (!confirm(`Delete “${r.name}”?${what}`)) return;
    this.grossBusy = true;
    try {
      const res = await fetch(`/api/owner-finance/formulas/${encodeURIComponent(id)}`, { method: 'DELETE' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { this.setGrossNote(j.error || `Not deleted (HTTP ${res.status}).`, true); return; }
      this.adoptGrossStore(j);   // R118 review #14: the version WITH the store it stamped
      await this.loadGross();
      const n = Number(j.unassigned) || 0;
      this.setGrossNote(`✓ Deleted “${r.name}”.${n ? ` ${n} propert${n === 1 ? 'y is' : 'ies are'} back on the default.` : ''}`);
    } catch (e: any) { this.setGrossNote(`Not deleted — ${(e && e.message) || 'network error'}.`, true); }
    finally { this.grossBusy = false; this.drawGross(); }
  }

  private async grossMakeDefault(id: string): Promise<void> {
    const r = this.grossRule(id);
    if (!r || this.grossBusy) return;
    this.grossBusy = true;
    try {
      const res = await this.grossPut({ default_rule: id });
      this.setGrossNote(res.ok ? `✓ “${r.name}” is now the default — every property without its own statement formula uses it.` : res.stale ? `${res.error} The list is reloaded — the default didn't change; try again.` : (res.error || 'Not saved.'), !res.ok);
    } finally { this.grossBusy = false; this.drawGross(); }
  }

  private async grossSaveRates(): Promise<void> {
    const d = this.grossRatesDraft;
    if (!d || !this.gross || this.grossBusy) return;
    const rates: Record<string, number | null> = { ...this.gross.channel_rates };
    for (const [k, l] of GRF_CH) {
      if (!(k in d)) continue;
      const v = String(d[k]).trim();
      if (v === '') { rates[k] = null; continue; }
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0 || n > 50) { this.setGrossNote(`${l}: enter a % between 0 and 50, or leave it blank.`, true); this.drawGross(); return; }
      rates[k] = n;
    }
    this.grossBusy = true;
    try {
      const res = await this.grossPut({ channel_rates: rates });
      if (res.ok) { this.grossRatesDraft = null; this.setGrossNote('✓ Channel fee rates saved.'); }
      else this.setGrossNote(res.stale ? `${res.error} The rest is reloaded — your rates are still in the boxes; click Save rates again.` : (res.error || 'Not saved.'), true);
    } finally { this.grossBusy = false; this.drawGross(); }
  }

  // ── gross: assign a formula to many properties (POST /assign, no version) ─
  private openGrossAssignModal(r: any): void {
    if (!r || !this.gross) return;
    const s = this.gross;
    const initial = new Set<string>((s.listings || []).map((l: any) => String(l.id)).filter((lid: string) => s.assign[lid] === r.id));
    this.grossPick = new Set(initial); this.grossPickSearch = '';
    this.openGrossModal('assign', String(r.id), `Assign · ${r.name}`, `
      <p class="grf-p" style="margin:0 0 10px;">Tick the properties that use <strong>${grfEsc(r.name)}</strong>. Unticking one sends it back to the default (${grfEsc(this.grossRuleName(s.default_rule))}).</p>
      <div class="grf-bar" style="margin-bottom:8px;">
        <input class="grf-in" id="grf-pick-search" type="search" placeholder="Search properties…" aria-label="Search properties" style="flex:1 1 160px; min-width:0;">
        <button class="btn btn-ghost btn-sm" data-grf="pick-all">Select shown</button>
        <button class="btn btn-ghost btn-sm" data-grf="pick-none">Clear shown</button>
      </div>
      <div class="grf-pick-list" id="grf-pick-list"></div>
      <div id="grf-m-err" class="grf-m-err" role="alert"></div>`,
      `<span id="grf-pick-count" class="grf-dim" style="margin-right:auto; align-self:center;"></span><button class="btn btn-secondary" data-grf="m-cancel">Cancel</button><button class="btn btn-primary" data-grf="assign-save">Save</button>`);
    if (this.grossModal) this.grossModal.initial = initial;
    this.drawGrossPickList();
  }
  private grossPickShown(): any[] {
    const q = this.grossPickSearch.trim().toLowerCase();
    return ((this.gross && this.gross.listings) || [])
      .filter((l: any) => !q || String(l.name || '').toLowerCase().includes(q))
      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || '')));
  }
  private drawGrossPickList(): void {
    const el = this.container.querySelector<HTMLElement>('#grf-pick-list');
    if (!el || !this.gross) return;
    const s = this.gross; const shown = this.grossPickShown();
    el.innerHTML = shown.length ? shown.map((l: any) => {
      const lid = String(l.id); const a = s.assign[lid];
      return `<label class="grf-pick"><input type="checkbox" data-grf-pick="${grfEsc(lid)}" ${this.grossPick.has(lid) ? 'checked' : ''}><span class="nm" title="${grfEsc(l.name)}">${grfEsc(l.name)}</span><span class="pill ${a ? 'pill-blue' : 'pill-gray'}" title="${grfEsc(this.grossRuleName(a || s.default_rule))}">${grfEsc(this.grossRuleName(a || s.default_rule))}${a ? '' : ' (default)'}</span></label>`;
    }).join('') : '<div class="grf-dim" style="padding:12px;">No properties match.</div>';
    this.drawGrossPickCount();
  }
  private grossPickDiff(): { added: string[]; removed: string[] } {
    const init = (this.grossModal && this.grossModal.initial) || new Set<string>();
    return { added: [...this.grossPick].filter(id => !init.has(id)), removed: [...init].filter(id => !this.grossPick.has(id)) };
  }
  private drawGrossPickCount(): void {
    const el = this.container.querySelector('#grf-pick-count');
    if (!el) return;
    const { added, removed } = this.grossPickDiff();
    el.textContent = `${this.grossPick.size} ticked${added.length || removed.length ? ` · ${added.length} to add, ${removed.length} back to default` : ''}`;
  }
  // POST /api/owner-finance/assign — patches ONLY `assign`; '' or the error to show
  private async grossAssign(ids: string[], ruleId: string | null): Promise<string> {
    try {
      const r = await fetch('/api/owner-finance/assign', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ listing_ids: ids, rule_id: ruleId }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return j.error || `Not assigned (HTTP ${r.status}).`;
      if (!this.adoptGrossStore(j) && j && j.assign && typeof j.assign === 'object' && this.gross) this.gross.assign = j.assign;   // R118 review #14
      return '';
    } catch (e: any) { return `Not assigned — ${(e && e.message) || 'network error'}.`; }
  }
  private async grossSaveAssign(): Promise<void> {
    const m = this.grossModal;
    if (!m || m.kind !== 'assign' || this.grossBusy) return;
    const { added, removed } = this.grossPickDiff();
    if (!added.length && !removed.length) { this.closeGrossModal(); return; }
    const btn = this.grossModalBtn('assign-save');
    this.grossBusy = true; if (btn) btn.disabled = true;
    try {
      if (added.length) { const e = await this.grossAssign(added, m.id); if (e) { this.grossModalErr(e); return; } for (const id of added) m.initial?.add(id); }
      if (removed.length) { const e = await this.grossAssign(removed, null); if (e) { this.grossModalErr(e); return; } for (const id of removed) m.initial?.delete(id); }
      this.closeGrossModal();
      const n = (k: number) => `${k} propert${k === 1 ? 'y' : 'ies'}`;
      this.setGrossNote(`✓ “${this.grossRuleName(m.id)}”: ${added.length ? `${n(added.length)} added` : ''}${added.length && removed.length ? ', ' : ''}${removed.length ? `${n(removed.length)} back to the default` : ''}.`);
    } finally {
      // R118 review #14: re-read EVEN when the second POST failed after the
      // first landed (the early return skipped it — the store had moved, this
      // page kept the old version and its own next save 409'd as "someone else")
      await this.loadGross();
      if (this.grossModal === m) this.drawGrossPickList();   // still open (an error): the pills follow the server
      this.grossBusy = false; if (btn) btn.disabled = false; this.drawGross();
      this.sb?.storeChanged();   // R119: the builder's "Assigned to N" + property list follow
    }
  }

  // ── gross: one property's facts (POST /profile) ───────────────────────────
  // R119: reachable for ANY property (the builder's "Edit property facts"),
  // not only after a Match: company fee, taxes, per-channel rates, commission
  // rate. R121: − the cleaning cost per turnover (no month estimates its
  // cleaning); + the company fee by booking date (set by Learn or a preview Fix —
  // shown here, and cleared here), the Vrbo fee on the guest total and a flat
  // owner fee per booking. The pre-R119 shape overrides
  // (cleaning in gross, fee on cleaning, commission base) beat the statement
  // formula when set, so they sit in their own fold, open when one is set.
  private openGrossProfileModal(lid: string): void {
    if (!lid || !this.gross) return;
    const saved = (this.gross.profiles || {})[lid] || null;
    const row = ((this.grossCalib && Array.isArray(this.grossCalib.listings) && this.grossCalib.listings) || []).find((x: any) => String(x.listing_id) === lid) || null;
    const p: any = saved || (row && row.learned) || {};
    const name = (row && row.name) || this.grossListingName(lid);
    const from = saved ? `Saved ${saved.source === 'manual' ? 'by hand' : 'from the statements'}.` : row ? 'Filled in with the learned values — not saved yet.' : 'Nothing saved yet.';
    const num = (k: GrfNumKey, label: string, step: string, hint: string) => `<div class="cfg-field"><label for="grf-p-${k}">${label}</label><input id="grf-p-${k}" type="number" min="0" max="${grfMax(k)}" step="${step}" inputmode="decimal" value="${grfNum(p[k]) == null ? '' : grfEsc(p[k])}" placeholder="not set"><div class="grf-hint">${hint}</div></div>`;
    const tri = (k: string, label: string, hint: string) => `<div class="cfg-field"><label for="grf-p-${k}">${label}</label><select id="grf-p-${k}"><option value="" ${p[k] == null ? 'selected' : ''}>not set</option><option value="yes" ${p[k] === true ? 'selected' : ''}>yes</option><option value="no" ${p[k] === false ? 'selected' : ''}>no</option></select><div class="grf-hint">${hint}</div></div>`;
    const cr = p.channel_rates && typeof p.channel_rates === 'object' ? p.channel_rates : {};
    const legacySet = p.cleaning_in_gross != null || p.cf_on_cleaning != null || !!p.commission_base;
    const sched = scheduleText(p.company_fee_schedule);
    this.openGrossModal('profile', lid, `Property facts · ${name}`, `
      <p class="grf-p" style="margin:0 0 12px;">${grfEsc(from)} Blank = not set — the statement formula, the commission formula and the shared channel rates decide.</p>
      <div class="grf-form">
        ${num('company_fee', 'Company fee per booking ($)', '0.01', 'kept out of the rent — damage waiver, the Mission Beach fee…')}
        ${num('commission_rate', 'Commission %', '0.1', 'e.g. 14 — this property\'s rate wins: over a statement formula\'s fixed rate and over the commission formula\'s rate')}
        ${num('tot_pct', 'Airbnb occupancy tax %', '0.01', 'the tax inside an Airbnb total with no tax line or payout (0 = Airbnb remits it)')}
        ${num('tot_pct_direct', 'Direct tax %', '0.01', 'inside a direct / website total with no tax line — blank: the Airbnb % when above 0, else 12.75%')}
        ${num('tot_pct_bookingcom', 'Booking.com tax %', '0.01', 'inside a Booking.com total with no tax line — blank: none (Booking.com itemizes it)')}
        <div class="cfg-field"><label for="grf-p-owner_fee_per_booking">Owner fee per booking ($)</label><input id="grf-p-owner_fee_per_booking" type="number" min="0" max="${FORMULA_BOUNDS.company_fee[1]}" step="0.01" inputmode="decimal" value="${grfNum(p.owner_fee_per_booking) == null ? '' : grfEsc(p.owner_fee_per_booking)}" placeholder="not set"><div class="grf-hint">a flat fee the statements pay the owner in Resolutions on every booking (S Shore $100)</div></div>
        <div class="cfg-field"><label for="grf-p-vrbo_fee_basis">Vrbo fee taken on</label><select id="grf-p-vrbo_fee_basis"><option value="" ${p.vrbo_fee_basis ? '' : 'selected'}>rents + cleaning (the formula's base)</option><option value="guest_total" ${p.vrbo_fee_basis === 'guest_total' ? 'selected' : ''}>the guest total</option></select><div class="grf-hint">VR Platform usually takes 7.335% of the Vrbo guest total</div></div>
      </div>
      <div class="cfg-field" id="grf-p-sched"><label>Company fee by booking date</label><div class="grf-p" style="margin:2px 0 4px;">${sched ? grfEsc(sched) : 'not set: every booking takes the company fee above'}</div>${sched ? '<label class="cfg-checkbox-row"><input type="checkbox" id="grf-p-sched-clear"> Clear it (every booking takes the company fee above)</label>' : ''}<div class="grf-hint">Learn or a preview Fix sets it when the statements show the fee changed (the fee in force when each booking was made).</div></div>
      <details class="grf-legacy" ${legacySet ? 'open' : ''}><summary>Older per-property overrides (before R119)</summary>
        <p class="grf-hint" style="margin:4px 0 8px;">When set, these beat the statement formula for this property. Leave them “not set” to let the formula decide.</p>
        <div class="grf-form">
          ${tri('cleaning_in_gross', 'Cleaning counts in gross', 'the statement has a Cleaning column')}
          ${tri('cf_on_cleaning', 'Channel fee on cleaning', 'the owner pays the channel fee on cleaning')}
          <div class="cfg-field"><label for="grf-p-commission_base">Commission is taken on</label><select id="grf-p-commission_base"><option value="" ${p.commission_base ? '' : 'selected'}>not set</option><option value="gross" ${p.commission_base === 'gross' ? 'selected' : ''}>gross</option><option value="net" ${p.commission_base === 'net' ? 'selected' : ''}>net (gross minus channel fee)</option></select></div>
        </div>
      </details>
      <div class="grf-lbl">Channel fee % for this property (blank = the formula's or the shared rate)</div>
      <div class="grf-rates">${GRF_PCH.map(([k, l]) => `<div><label for="grf-p-ch-${k}">${l}</label><input class="grf-in" id="grf-p-ch-${k}" type="number" min="0" max="${GRF_PROFILE_MAX.channel}" step="0.1" inputmode="decimal" value="${grfNum(cr[k]) == null ? '' : grfEsc(cr[k])}" placeholder="shared" style="width:100%;"></div>`).join('')}</div>
      <div id="grf-m-err" class="grf-m-err" role="alert"></div>`,
      `${saved ? '<button class="btn btn-outline" data-grf="profile-clear" style="margin-right:auto;">Clear settings</button>' : ''}<button class="btn btn-secondary" data-grf="m-cancel">Cancel</button><button class="btn btn-primary" data-grf="profile-save">Save</button>`);
  }

  private async grossSaveProfile(clear: boolean): Promise<void> {
    const m = this.grossModal; const body = this.container.querySelector<HTMLElement>('#grf-modal-body');
    if (!m || m.kind !== 'profile' || !body || this.grossBusy) return;
    let profile: Record<string, unknown> | null = null;
    if (clear) {
      if (!confirm('Clear this property\'s facts? It goes back to the formula defaults.')) return;
    } else {
      const val = (sel: string) => String(body.querySelector<HTMLInputElement>(sel)?.value ?? '').trim();
      // null = blank (not set), undefined = invalid
      const n = (k: string, max: number): number | null | undefined => { const v = val(`#grf-p-${k}`); if (v === '') return null; const x = Number(v); return Number.isFinite(x) && x >= 0 && x <= max ? x : undefined; };
      const tri = (k: string): boolean | null => { const v = val(`#grf-p-${k}`); return v === 'yes' ? true : v === 'no' ? false : null; };
      // R118 review #17: the server's bounds (lib/ownerFinance normProfile), so a
      // value it refuses is said here in words — not its raw "Invalid profile values: tot_pct"
      const M = GRF_PROFILE_MAX;
      const fee = n('company_fee', M.company_fee), tot = n('tot_pct', M.tot_pct), rate = n('commission_rate', M.commission_rate);
      // R118 review #2: the direct / Booking.com totals' own tax % — tot_pct's bound
      const totD = n('tot_pct_direct', grfMax('tot_pct_direct')), totB = n('tot_pct_bookingcom', grfMax('tot_pct_bookingcom'));
      if (fee === undefined) { this.grossModalErr(`Company fee: a dollar amount from 0 to ${M.company_fee.toLocaleString()}, or blank.`); return; }
      if (tot === undefined) { this.grossModalErr(`Occupancy tax: a % from 0 to ${M.tot_pct}, or blank.`); return; }
      if (totD === undefined) { this.grossModalErr(`Direct tax: a % from 0 to ${grfMax('tot_pct_direct')}, or blank.`); return; }
      if (totB === undefined) { this.grossModalErr(`Booking.com tax: a % from 0 to ${grfMax('tot_pct_bookingcom')}, or blank.`); return; }
      if (rate === undefined) { this.grossModalErr(`Commission: a % from 0 to ${M.commission_rate}, or blank.`); return; }
      const ch: Record<string, number | null> = {}; let anyCh = false;
      for (const [k, l] of GRF_PCH) {
        const x = n(`ch-${k}`, M.channel);
        if (x === undefined) { this.grossModalErr(`${l} channel fee: a % from 0 to ${M.channel}, or blank.`); return; }
        ch[k] = x; if (x != null) anyCh = true;
      }
      // R121: a flat owner fee per booking (lib/ownerFinance profile bound, FORMULA_BOUNDS.company_fee); the Vrbo fee's base;
      // the schedule only when cleared ([] clears it; left out, the saved one stays). No cleaning cost (retired).
      const of = n('owner_fee_per_booking', FORMULA_BOUNDS.company_fee[1]);
      if (of === undefined) { this.grossModalErr(`Owner fee per booking: a dollar amount from 0 to ${FORMULA_BOUNDS.company_fee[1].toLocaleString()}, or blank.`); return; }
      const base = val('#grf-p-commission_base');
      const clearSched = !!body.querySelector<HTMLInputElement>('#grf-p-sched-clear')?.checked;
      profile = { company_fee: fee, tot_pct: tot, tot_pct_direct: totD, tot_pct_bookingcom: totB, cleaning_in_gross: tri('cleaning_in_gross'), cf_on_cleaning: tri('cf_on_cleaning'), commission_rate: rate, commission_base: base === 'gross' || base === 'net' ? base : null, channel_rates: anyCh ? ch : null,
        owner_fee_per_booking: of, vrbo_fee_basis: val('#grf-p-vrbo_fee_basis') === 'guest_total' ? 'guest_total' : null, ...(clearSched ? { company_fee_schedule: [] } : {}) };
    }
    const btn = this.grossModalBtn(clear ? 'profile-clear' : 'profile-save');
    this.grossBusy = true; if (btn) btn.disabled = true;
    try {
      const r = await fetch('/api/owner-finance/profile', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ listing_id: m.id, profile }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { this.grossModalErr(j.error || `Not saved (HTTP ${r.status}).`); return; }
      // R118 review #17: report what the SERVER did. Every field blank → it
      // stores nothing (deletes the profile) and answers profile: null; this
      // used to invent a hand-set profile and say "Apply keeps them" — the
      // next Apply then wrote learned values over a property shown as kept.
      const had = !!((this.gross && this.gross.profiles) || {})[m.id];
      const saved = j && j.profile && typeof j.profile === 'object' ? j.profile : null;
      if (!this.adoptGrossStore(j) && this.gross) { const ps = { ...(this.gross.profiles || {}) }; if (saved) ps[m.id] = saved; else delete ps[m.id]; this.gross.profiles = ps; }   // R118 review #14
      const row = ((this.grossCalib && Array.isArray(this.grossCalib.listings) && this.grossCalib.listings) || []).find((x: any) => String(x.listing_id) === m.id);
      if (row) row.current = saved;
      const name = (row && row.name) || this.grossListingName(m.id);
      await this.loadGross();   // fresh version + profiles (the adopted store above stands if this read fails)
      this.closeGrossModal();
      this.setGrossNote(saved
        ? `✓ Saved ${name}'s facts — set by hand, so “Apply learned settings” keeps them${this.grossOverwriteManual ? ' (unless “Also replace the ones set by hand” is ticked)' : ''}.`
        : clear || had
          ? `✓ Cleared ${name}'s facts — nothing is set, so it uses the formula defaults; “Apply learned settings” can fill them in from the statements.`
          : `Nothing set — every field was blank, so ${name} uses the formula defaults; “Apply learned settings” can fill them in from the statements.`);
      this.sb?.refresh();   // R119: the builder's preview re-runs on the new facts
      if (row) await this.grossRecheckRow(m.id);   // R118 review #11: what Apply does for this row just changed — ask the server
    } catch (e: any) { this.grossModalErr(`Not saved — ${(e && e.message) || 'network error'}.`); }
    finally { this.grossBusy = false; if (btn) btn.disabled = false; this.drawGross(); }
  }

  // R118 review #11 / #17: a per-property save changes what Apply will do for
  // that row (a hand-set profile is kept; a deleted one gets the learned
  // values), so the row's verdict from the earlier check is stale. Ask the
  // server again for THAT row (a dry run scoped to it, the same months) — no
  // client guess; if that fails the row says "run the check again" and is not
  // counted toward Apply.
  private async grossRecheckRow(lid: string): Promise<void> {
    const c = this.grossCalib;
    if (!c || c.dry_run !== true || c.running || !Array.isArray(c.listings)) return;
    const i = c.listings.findIndex((x: any) => String(x.listing_id) === lid);
    if (i < 0) return;
    const body: Record<string, unknown> = { dry_run: true, listing_ids: [lid] };
    if (Array.isArray(c.months) && c.months.length) body.months = c.months;
    let fresh: any = null;
    try {
      const r = await fetch('/api/owner-finance/calibrate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j && Array.isArray(j.listings)) fresh = j.listings.find((x: any) => String(x.listing_id) === lid) || null;
    } catch { /* marked unknown below */ }
    if (this.grossCalib !== c) return;   // closed or re-run meanwhile
    const cur = c.listings[i];
    c.listings[i] = fresh && typeof fresh.will_apply === 'boolean' ? fresh : { ...cur, will_apply: null, will_apply_overwrite: null, why: null, why_overwrite: null, will_apply_assign: null, will_apply_assign_overwrite: null, ...(cur && cur.suggested_formula ? { will_assign: null, why_assign: null, will_assign_overwrite: null } : {}) };
  }

  // ── gross: learn from the statements (dry run → confirm → apply) ──────────
  private async grossCalibrate(dry: boolean): Promise<void> {
    if (!this.gross || this.grossBusy) return;
    const prev = this.grossCalib;
    const body: Record<string, unknown> = { dry_run: dry };
    if (dry) {
      if (this.grossCalibMonths) body.months = grfMonthsBack(Number(this.grossCalibMonths));
      this.grossCalibUntick.clear(); this.grossCalibAssign = false;   // R119: a fresh check starts with every row ticked
    } else {
      const n = this.grossApplyCount(prev);   // R118 review #11: the server's own verdicts (R119: minus the unticked rows)
      const assign = this.grossCalibAssign && ((prev && Array.isArray(prev.listings)) ? prev.listings : []).some((l: any) => l && l.suggested_formula);
      // R119 fix-verify: with "Also assign", "Also replace" reaches hand-set FACTS only — a
      // statement formula chosen by hand is kept either way (said below)
      const what = assign ? 'Facts' : 'Settings';
      const hand = this.grossOverwriteManual ? ` ${what} someone set by hand are replaced too.` : ` ${what} someone set by hand are kept.`;
      // R119 review #14: facts and statement formulas counted apart — the confirm says both
      const plan = this.grossApplyPlan(prev);
      const msg = assign
        // R119 fix-verify: a ticked row whose re-check failed goes out too — the server may write
        // its facts as well as its formula, so the facts count says "up to" for it as well
        ? `Apply to ${grfProps(n)}: the learned facts for ${plan.facts}${plan.factsMaybe ? ` (+ up to ${plan.factsMaybe} whose check needs re-running)` : ''}, and ${plan.assign} pointed at ${plan.assign === 1 ? 'its' : 'their'} suggested statement formula${plan.maybe ? ` (+ up to ${plan.maybe} whose check needs re-running)` : ''}${plan.created.length ? ` — creating ${plan.created.map(x => `“${x}”`).join(', ')}` : ''}?${hand} A statement formula assigned by hand is kept. Owner portals use them from now on.`
        : `Apply the learned statement settings to ${grfProps(n)}?${hand} Owner portals use them from now on.`;
      if (!confirm(msg)) return;
      if (prev && Array.isArray(prev.months) && prev.months.length) body.months = prev.months;   // apply exactly what the check showed
      else if (this.grossCalibMonths) body.months = grfMonthsBack(Number(this.grossCalibMonths));
      if (this.grossOverwriteManual) body.overwrite_manual = true;
      // R119: an unticked row is left out (listing_ids = the ticked rows the server would apply).
      // R119 review #14: with "Also assign" the ticked rows are ALWAYS sent — the server assigns
      // every listing in scope, so without listing_ids a row with no tick was re-pointed (and an
      // unrelated untick changed which); it now requires listing_ids with assign_formulas
      const all = ((prev && Array.isArray(prev.listings)) ? prev.listings : []).filter((l: any) => this.grossVerdict(prev, l).apply === true);
      if (assign || (this.grossCalibUntick.size && all.some((l: any) => this.grossCalibUntick.has(String(l.listing_id))))) body.listing_ids = this.grossApplyRows(prev).map((l: any) => String(l.listing_id));
      if (assign) body.assign_formulas = true;
    }
    this.grossBusy = true;
    this.grossCalib = { running: true, dry_run: dry };
    this.drawGross();
    try {
      const r = await fetch('/api/owner-finance/calibrate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      this.grossCalib = { ...j, dry_run: dry ? true : false };
      if (!dry) {
        this.adoptGrossStore(j);   // R118 review #14: the new version WITH the store it stamps — a failed re-read below can't leave it stale
        await this.loadGross();   // the new profiles + calibration summary (+ fresh version)
        const a = Number(j.applied) || 0;
        // R119 review #14: with "Also assign", what happened to the statement formulas too
        const sk: any[] = Array.isArray(j.assign_skipped) ? j.assign_skipped : [];
        const cr: any[] = Array.isArray(j.created) ? j.created : [];
        const asg = Array.isArray(j.assign_skipped) ? ` Statement formula: ${Number(j.assigned) || 0} re-pointed${cr.length ? `, ${cr.length} created` : ''}${sk.length ? `, ${sk.length} not assigned (see below)` : ''}.` : '';
        this.setGrossNote(`✓ Applied the learned settings to ${grfProps(a)}.${asg}`);
      }
    } catch (e: any) {
      const msg = (e && e.message) || 'network error';
      if (dry) this.grossCalib = { error: `Couldn't check the statements — ${msg}` };
      else { this.grossCalib = prev; this.setGrossNote(`Not applied — ${msg}`, true); }   // a failed apply keeps the check on screen
    } finally { this.grossBusy = false; this.drawGross(); }
  }

  private async grossReconcile(): Promise<void> {
    const month = this.grossRecMonth;
    if (!/^\d{4}-\d{2}$/.test(month)) { this.grossRec = { error: 'Pick a month first.' }; this.drawGross(); return; }
    this.grossRec = { loading: true }; this.drawGross();
    try {
      const r = await fetch(`/api/owner-finance/reconcile?month=${encodeURIComponent(month)}`);
      const j = await r.json().catch(() => ({}));
      this.grossRec = r.ok ? j : { error: j.error || `HTTP ${r.status}` };
    } catch (e: any) { this.grossRec = { error: (e && e.message) || 'network error' }; }
    this.drawGross();
  }

}
